/**
 * Domain hooks — TanStack Query wrappers for all main endpoints.
 */
import type { PayBasis, DaySummary } from '../lib/pay-basis';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, uploadFile } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import { currentPeriod } from '../lib/period';
import type {
  SubmitLeaveRequest,
  SubmitOTRequest,
  SubmitCheckinRequest,
  ApprovalActionRequest,
  AuthProfile,
} from '@payroll/shared';

// === Multi-tenant membership (2026-05-23) ===
export interface TenantMembership {
  user_id: string;
  tenant_id: string;
  tenant_code: string;
  tenant_slug: string;
  tenant_name: string;
  tenant_status: string;
  role: 'OWNER' | 'HR' | 'MANAGER' | 'SUPERVISOR' | 'EMPLOYEE';
  emp_code: string | null;
  is_current: boolean;
}
export function useMyTenants() {
  return useQuery({
    queryKey: ['me', 'tenants'],
    queryFn: () => apiFetch<{ memberships: TenantMembership[] }>('/me/tenants').then((d) => d.memberships),
    staleTime: 5 * 60_000,
  });
}
// === Leave Packages (HR) ===
export interface LeavePackage {
  id: string;
  name: string;
  description: string | null;
  sick_quota: number;
  personal_quota: number;
  vacation_quota: number;
  prorate_on_join: boolean;
  carry_over_max: number;
  vacation_first_year_eligible: boolean;
  updated_at: string;
  member_count: number;
}
export function useLeavePackages() {
  return useQuery({
    queryKey: ['hr', 'leave-packages'],
    queryFn: () => apiFetch<{ packages: LeavePackage[] }>('/hr/leave-packages').then((d) => d.packages),
    staleTime: 60_000,
  });
}
export interface LeavePackageMember {
  emp_code: string;
  name: string;
  department: string | null;
  vacation_quota: number;
  prorate_factor: number;
  carried_days: number;
}
export function usePackageMembers(packageId: string | null, year: number) {
  return useQuery({
    queryKey: ['hr', 'leave-packages', packageId, 'members', year],
    enabled: !!packageId,
    queryFn: () => apiFetch<{ members: LeavePackageMember[]; year: number }>(`/hr/leave-packages/${packageId}/members?year=${year}`),
  });
}
export function useUpsertLeavePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<LeavePackage> & { name: string }) =>
      apiFetch<LeavePackage>('/hr/leave-packages', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'leave-packages'] }),
  });
}
export function useDeleteLeavePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean }>(`/hr/leave-packages/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'leave-packages'] });
      qc.invalidateQueries({ queryKey: ['hr', 'quotas'] });
    },
  });
}
export function useAssignLeavePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { package_id: string; year: number; emp_codes: string[]; apply_prorate: boolean }) =>
      apiFetch<{ assigned: number; prorated: boolean; year: number }>('/hr/leave-packages/assign', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'quotas'] });
      qc.invalidateQueries({ queryKey: ['hr', 'leave-packages'] });
    },
  });
}
export function useRolloverLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (from_year: number) =>
      apiFetch<{ rolled: number; from_year: number; to_year: number }>('/hr/leave-quotas/rollover', { method: 'POST', body: { from_year } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'quotas'] }),
  });
}

export function useSwitchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenant_id: string) =>
      apiFetch<{ jwt: string; profile: AuthProfile }>('/me/switch-tenant', {
        method: 'POST',
        body: { tenant_id },
      }),
    onSuccess: (data) => {
      // Re-bind the auth store to the new tenant + nuke every cached query so
      // the next render fetches data scoped to the new tenant_id.
      useAuthStore.getState().setAuth(data.jwt, data.profile);
      qc.invalidateQueries();
    },
  });
}

// === Close readiness (เรื่อง 10) — what must be cleared before locking ===
export interface ReadinessItem {
  key: string; label: string; severity: 'red' | 'yellow'; affectsCycle: boolean; hardBlock?: boolean; count: number; detail: Record<string, unknown>;
}
export interface CloseReadiness {
  period: string; ready: boolean; affectsCycleCount: number; hardBlockCount: number; totalCount: number; items: ReadinessItem[];
  dueNow: boolean; cutoffDay: number; mode: 'salary_only' | 'attendance';
}
export function useCloseReadiness(period: string) {
  return useQuery({
    queryKey: ['close-readiness', period],
    queryFn: () => apiFetch<CloseReadiness>(`/owner/close-readiness?period=${period}`),
    staleTime: 60_000,
  });
}

// === Variance Bridge (A13) — Owner payroll analysis ===
export interface BridgeDriver { key: string; label: string; amount: number }
export interface VarianceOutlier { emp_code: string; prior: number; current: number; delta: number; pct: number | null }
export interface VarianceBridge {
  period: string; priorPeriod: string;
  curNet: number; priorNet: number; deltaNet: number; deltaPct: number | null;
  curHeadcount: number; priorHeadcount: number;
  drivers: BridgeDriver[]; reconciled: boolean;
  otRatio: number; costPerHead: number; outliers: VarianceOutlier[];
}
export interface PayrollAnalysis { bridge: VarianceBridge; summary: string | null; mode: 'rule-only' | 'llm' }
export function usePayrollAnalysis(period: string) {
  return useQuery({
    queryKey: ['payroll-analysis', period],
    queryFn: () => apiFetch<PayrollAnalysis>(`/ai/payroll/analysis?period=${period}`),
    staleTime: 60_000,
  });
}

// === Quota ===
export function useQuota() {
  return useQuery({
    queryKey: ['quota'],
    queryFn: () => apiFetch('/quota'),
  });
}

// fb#345 — ช่วงครึ่งวันเช้า/บ่ายตามกะของวันนั้น (null = ไม่มีกะ → นับ 0.5 วัน)
export interface HalfDayWindows {
  hours_per_day: number;
  morning: { start_time: string; end_time: string } | null;
  afternoon: { start_time: string; end_time: string } | null;
}
export function useHalfDayWindows(date: string | undefined) {
  return useQuery({
    queryKey: ['leave', 'half-day', date],
    queryFn: () => apiFetch<HalfDayWindows>(`/leave/half-day?date=${encodeURIComponent(date!)}`),
    enabled: !!date,
  });
}

// === Daily briefing (AI layer A6) — open findings scoped to caller's role ===
export interface BriefingFinding {
  id: string;
  rule_code: string;
  severity: 'INFO' | 'WARN' | 'ERROR';
  title: string;
  suggested_action: { kind?: string; [k: string]: unknown } | null;
  period: string | null;
}
export interface BriefingData {
  mode: 'rule-only' | 'llm';
  summary: string | null;
  findings: BriefingFinding[];
}
export function useBriefing() {
  return useQuery({
    queryKey: ['briefing'],
    queryFn: () => apiFetch<BriefingData>('/ai/briefing'),
    staleTime: 60_000,
  });
}
export function useUpdateFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: 'done' | 'dismissed'; reason?: string }) =>
      apiFetch(`/ai/findings/${v.id}`, { method: 'POST', body: { status: v.status, reason: v.reason } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['briefing'] }),
  });
}

// === BYOK settings (AI layer A14) — OWNER only ===
export interface AISettings {
  enabled: boolean;
  provider: string | null;
  model: string | null;
  monthly_token_cap: number;
  tokens_used_this_month: number;
  last_error: string | null;
  key_set: boolean;
  key_last4: string | null;
  seed_tokens_remaining: number;
  on_seed_trial: boolean;
  ask_daily_limit: number;
}
export function useAISettings() {
  return useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => apiFetch<AISettings>('/ai/settings'),
  });
}
export function useUpdateAISettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<{ enabled: boolean; provider: string; model: string; monthly_token_cap: number; api_key: string; ask_daily_limit: number }>) =>
      apiFetch<AISettings>('/ai/settings', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-settings'] }),
  });
}
export function useTestAIKey() {
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean; message: string }>('/ai/settings/test', { method: 'POST' }),
  });
}

// === Ask AI (A8) — grounded Q&A over read-tools scoped to the caller's role ===
export interface AskAIResult {
  available: boolean;
  answer: string | null;
  message?: string;
  tools_used?: string[];
  rate_limited?: boolean;
  quota?: { limit: number; used: number; remaining: number };
}
export function useAskAI() {
  return useMutation({
    mutationFn: (question: string) => apiFetch<AskAIResult>('/ai/ask', { method: 'POST', body: { question } }),
  });
}

// === Setup checklist (AI layer A17) — deterministic, no key needed ===
export interface SetupItem { key: string; label: string; done: boolean; route: string; optional?: boolean; manual?: boolean }
export interface SetupChecklist { items: SetupItem[]; pct: number; complete: boolean; nextStep: SetupItem | null }
export function useAckSetupStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { key: string; done: boolean }) =>
      apiFetch<{ acked: string[] }>('/ai/setup/ack', { method: 'POST', body: args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-setup'] }),
  });
}
// === Role permission preview (A19) — derived matrix for assign-time UI ===
export interface RoleMatrixItem { key: string; label: string; allowed: boolean; why: string }
export interface RoleMatrix { role: string; known: boolean; items: RoleMatrixItem[] }
export function useRolePermissions(role: string) {
  return useQuery({
    queryKey: ['ai-permissions', role],
    queryFn: () => apiFetch<RoleMatrix>(`/ai/permissions/${role}`),
    enabled: !!role,
    staleTime: 5 * 60_000,
  });
}

// === Wayfinding LLM fallback (A18) — HumanAI-funded, menu-only ===
export function useWayfind() {
  return useMutation({
    mutationFn: (v: { query: string; candidates: { label: string; route: string }[] }) =>
      apiFetch<{ matches: { label: string; route: string }[] }>('/ai/wayfind', { method: 'POST', body: v }),
  });
}

export function useSetupChecklist() {
  return useQuery({
    queryKey: ['ai-setup'],
    queryFn: () => apiFetch<SetupChecklist>('/ai/setup'),
    staleTime: 60_000,
  });
}

// === History (leave + OT submitted by self) ===
export function useHistory() {
  return useQuery({
    queryKey: ['history'],
    queryFn: () => apiFetch('/history'),
  });
}

// === Submit leave ===
export function useSubmitLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitLeaveRequest) =>
      apiFetch('/leave', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quota'] });
      qc.invalidateQueries({ queryKey: ['history'] });
    },
  });
}

// === My Requests / My Decisions (TASK 25) ===
//
// /me/requests  = ใบที่ฉันส่ง (leave + OT + escalation)
// /me/decisions = ใบที่ฉันตัดสิน (l*_approver=me + resolved_by=me + reviewed_by=me)
//
// ทุก role เปิดได้. EMPLOYEE ที่ไม่เคย approve → /me/decisions คืน items=[] (ไม่ใช่ 403)
export interface TimelineStep {
  level: 1 | 2 | 3;
  approver_line_user_id: string | null;
  approver_name: string | null;
  decided_at: string | null;
  decision: 'pending' | 'approved' | 'rejected' | 'info_requested' | 'conditionally_approved';
}
export interface MyRequestItem {
  kind: 'leave' | 'ot' | 'escalation';
  id: string;
  record_id: string;
  request_group_id?: string;
  date: string;
  end_date?: string;
  days_count?: number;
  type: string;
  reason: string | null;
  status: string;
  current_level: number;
  required_levels: number;
  start_time?: string | null;
  end_time?: string | null;
  period?: string;
  is_emergency?: boolean;
  is_backdated?: boolean;
  submitted_at: string;
  final_decided_at: string | null;
  rejection_reason: string | null;
  resolution_note?: string | null;
  conditional_evidence_due?: string | null;
  evidence_url?: string | null;
  cancel_requested?: boolean;   // owner/HR ขอยกเลิกใบที่อนุมัติแล้ว รอเรายินยอม
  cancel_reason?: string | null;
  /** fb#438 — การ์ดเข้างานไม่ครบเวลาที่ตัดสินแล้ว */
  decision?: 'deduct' | 'waive' | null;
  approval_timeline: TimelineStep[];
}
export interface MyDecisionItem {
  kind: 'leave' | 'ot' | 'emergency_review' | 'escalation';
  id: string;
  record_id: string;
  target_emp_code: string;
  target_emp_name: string;
  target_department: string | null;
  date: string;
  end_date?: string | null;
  type: string;
  reason: string | null;
  start_time?: string | null;
  end_time?: string | null;
  my_role_in_decision: 'L1' | 'L2' | 'L3' | 'reviewer' | 'resolver';
  decision: 'approved' | 'rejected' | 'info_requested' | 'conditionally_approved' | 'resolved' | 'ignored';
  decided_at: string;
  decision_note: string | null;
  current_status: string;
  current_level: number;
  required_levels: number;
}
export interface MyRequestsResp {
  items: MyRequestItem[];
  next_cursor: string | null;
  summary: { pending_count: number; needs_action: number; total: number };
}
export interface MyDecisionsResp {
  items: MyDecisionItem[];
  next_cursor: string | null;
  summary: {
    total: number;
    approved: number;
    rejected: number;
    info_requested: number;
    resolved: number;
    ignored: number;
  };
}
export function useMyRequests(params: { status?: string; kind?: string; from?: string; to?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.kind) qs.set('kind', params.kind);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['my-requests', params],
    queryFn: () => apiFetch<MyRequestsResp>(`/me/requests${suffix}`),
    staleTime: 30_000,
  });
}
// ยกเลิกคำขอลา/OT ของตัวเอง (เฉพาะใบที่ยังรออนุมัติ)
export function useCancelRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: 'leave' | 'ot'; record_id: string }) =>
      apiFetch('/me/requests/cancel', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-requests'] });
      qc.invalidateQueries({ queryKey: ['quota'] });
      qc.invalidateQueries({ queryKey: ['history'] });
    },
  });
}

// พนักงานตอบคำขอยกเลิก (ของ owner/HR) ที่ค้างอยู่บนใบที่อนุมัติแล้ว
export function useConsentCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: 'leave' | 'ot'; record_id: string; consent: boolean }) =>
      apiFetch('/me/requests/consent-cancel', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-requests'] });
      qc.invalidateQueries({ queryKey: ['quota'] });
      qc.invalidateQueries({ queryKey: ['history'] });
    },
  });
}

// === Owner/HR: ใบที่อนุมัติแล้ว แต่ยังมาไม่ถึง (ขอยกเลิกได้ ต้องให้พนักงานยินยอม) ===
export interface UpcomingApprovedLeave {
  record_id: string;
  leave_id: string;
  request_group_id: string | null;
  emp_code: string;
  emp_name: string;
  date: string;
  end_date: string;
  days_count: number;
  type: string;
  type_label: string;
  reason: string | null;
  cancel_pending: boolean;
  cancel_reason: string | null;
}
export interface UpcomingApprovedOt {
  record_id: string;
  ot_id: string;
  emp_code: string;
  emp_name: string;
  date: string;
  type: string;
  type_label: string;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
  cancel_pending: boolean;
  cancel_reason: string | null;
}
export function useUpcomingApproved() {
  return useQuery({
    queryKey: ['upcoming-approved'],
    queryFn: () => apiFetch<{ leaves: UpcomingApprovedLeave[]; ots: UpcomingApprovedOt[] }>('/approvals/upcoming-approved'),
    staleTime: 30_000,
  });
}
// === สรุปการอนุมัติ รายเดือน/รายปี (ลา/OT ที่อนุมัติแล้ว + มาสาย/ขาดงาน) ===
export interface MonthlyApprovalSummary {
  mode: 'month' | 'year';
  month: string;
  year: number;
  /** `ot` = จำนวนใบ · `ot_hours` = ชั่วโมงรวมจากใบที่อนุมัติ */
  summary: { leave: number; leave_days: number; ot: number; ot_hours: number; late: number; absent: number; late_open: number; absent_open: number };
  /** เฉพาะมุมมองรายปี — สรุปแยก 12 เดือน */
  by_month?: Array<{ month: string; leave: number; leave_days: number; ot: number; ot_hours: number; late: number; absent: number; late_open: number; absent_open: number }>;
  /** รายการรายวัน — มุมมองรายปีส่งเป็น [] (ดูรายละเอียดในมุมมองรายเดือน) */
  leaves: Array<{ emp_code: string; emp_name: string; date: string; end_date: string; days: number; type: string; type_label: string; reason: string | null }>;
  ots: Array<{ emp_code: string; emp_name: string; date: string; type: string; type_label: string; start_time: string | null; end_time: string | null; hours: number; reason: string | null }>;
  late: Array<{ emp_code: string; emp_name: string; date: string; detail: string | null; status: string; resolved: boolean }>;
  absent: Array<{ emp_code: string; emp_name: string; date: string; detail: string | null; status: string; resolved: boolean }>;
  by_emp: Array<{ emp_code: string; emp_name: string; department?: string; position?: string; leave_days: number; leave_by_type: Record<string, number>; ot_count: number; ot_hours: number; late_count: number; absent_count: number; late_open: number; absent_open: number }>;
}
/** period = 'YYYY-MM' (รายเดือน) หรือ 'YYYY' (รายปี) */
export function useApprovalSummary(period: string) {
  const isYear = /^\d{4}$/.test(period);
  return useQuery({
    queryKey: ['approval-summary', period],
    queryFn: () =>
      apiFetch<MonthlyApprovalSummary>(
        `/approvals/monthly-summary?${isYear ? 'year' : 'month'}=${encodeURIComponent(period)}`,
      ),
    staleTime: 30_000,
  });
}
/**
 * ดึงสรุป + รายการรายวัน สำหรับทำไฟล์ report (มุมมองรายปีปกติไม่ส่งรายการรายวันมา
 * เพราะทั้งปีเป็นพันแถว — ขอ detail=1 เฉพาะตอนกดดึงไฟล์)
 */
export function fetchApprovalSummaryDetail(period: string) {
  const isYear = /^\d{4}$/.test(period);
  return apiFetch<MonthlyApprovalSummary>(
    `/approvals/monthly-summary?${isYear ? 'year' : 'month'}=${encodeURIComponent(period)}&detail=1`,
  );
}

/** รายงานเวลาเข้า-ออกทั้งบริษัทรายงวด (ทุกคน × ทุกวัน) — ใช้ตอนกดดึงไฟล์เท่านั้น
 *  จึงเป็น fetch ตรง ไม่ cache ไว้ใน react-query (ข้อมูลใหญ่และดูครั้งเดียว) */
export interface AttendanceExportRow {
  date: string;
  emp_code: string;
  emp_name: string;
  department: string;
  position: string;
  shift_code: string;
  shift_start: string;
  shift_end: string;
  in_at: string;
  out_at: string;
  late_minutes: number;
  status: string;
  status_label: string;
  leave_type: string;
  /** ชั่วโมงตามใบ OT ที่อนุมัติของวันนั้น (0 = ไม่มี) */
  ot_hours: number;
  /** ชนิด OT ของวันนั้น หลายใบคนละชนิดคั่นด้วย "+" */
  ot_type: string;
  note: string;
}
export interface AttendanceExportEmp {
  emp_code: string;
  emp_name: string;
  department: string;
  position: string;
  counted: {
    working_days: number;
    incomplete_scan_days: number;
    leave_days: number;
    absent_days: number;
    late_days: number;
    late_minutes: number;
    holiday_days: number;
    /** ชั่วโมงรวมจากใบ OT ที่อนุมัติในช่วง — วางเทียบกับ payroll.ot_hours (ที่ระบบใช้คิดเงิน) */
    ot_hours: number;
    ot_count: number;
  };
  payroll: {
    working_days: number;
    absent_days: number;
    paid_leave_days: number;
    unpaid_leave_days: number;
    late_days: number;
    late_minutes: number;
    ot_hours: number;
  } | null;
}
export interface AttendanceExport {
  /** งวดปฏิทิน — ว่างเมื่อดึงด้วยช่วงวันที่ที่กำหนดเอง */
  period: string;
  from: string;
  to: string;
  /** false = ช่วงที่กำหนดเอง เทียบกับตัวเลขฝั่งคิดเงินไม่ได้ (คนละช่วงวันที่กับงวด) */
  payroll_comparable: boolean;
  reconciled: boolean;
  /** ตัวเลขฝั่ง "ระบบคิดเงิน" เป็นภาพ ณ เวลาไหน (null = ยังไม่เคยสรุปเวลางวดนี้) */
  summary_computed_at: string | null;
  /** true = มีสแกน/ใบลาเปลี่ยนหลังสรุปเวลา → ตัวเลขฝั่งระบบยังไม่ตามข้อมูลล่าสุด */
  summary_stale: boolean;
  summary_stale_reason: string | null;
  rows: AttendanceExportRow[];
  by_emp: AttendanceExportEmp[];
  generated_at: string;
}
/** ภาพปัจจุบันของงวด — คำนวณสดทุกครั้งที่เรียก (ไม่ใช่ snapshot ตอนสรุปเวลา) */
export interface AttendanceLiveEmp {
  emp_code: string;
  emp_name: string;
  department: string;
  position: string;
  working_days: number;
  incomplete_scan_days: number;
  leave_days: number;
  absent_days: number;
  late_days: number;
  late_minutes: number;
  holiday_days: number;
}
export interface AttendanceLive {
  period: string;
  from: string;
  to: string;
  computed_at: string;
  totals: {
    headcount: number; working_days: number; incomplete_scan_days: number;
    leave_days: number; absent_days: number; late_days: number; late_minutes: number;
  };
  by_emp: AttendanceLiveEmp[];
  payroll_snapshot: {
    exists: boolean;
    computed_at: string | null;
    stale: boolean;
    stale_reason: string | null;
    mismatch_count: number;
    /** B2 — งวดปิด/จ่ายแล้ว (monthly_summary.locked) — ห้ามรันสรุปเวลาทับ */
    locked: boolean;
    /** fb#438 — เปิดหักเข้างานไม่ครบเวลางวดนี้ + นาทีที่หายตามสรุปเวลาล่าสุด (ก่อนหักวันที่ยกเว้น) */
    shortfall_enabled?: boolean;
    shortfall_minutes?: number;
    shortfall_by_emp?: Record<string, number>;
  };
  mismatches: Array<{
    emp_code: string; emp_name: string;
    absent_diff: number; leave_diff: number; working_diff: number;
    live: { absent_days: number; leave_days: number; working_days: number };
    payroll: { absent_days: number; leave_days: number; working_days: number };
  }>;
}
export function useAttendanceLive(period: string) {
  return useQuery({
    queryKey: ['attendance-live', period],
    queryFn: () => apiFetch<AttendanceLive>(`/hr/attendance/live?period=${encodeURIComponent(period)}`),
    staleTime: 15_000, // ภาพปัจจุบัน — refetch บ่อยได้ แต่ไม่ต้องยิงซ้ำทุกคลิก
  });
}

/**
 * ดึงรายงานเวลาเข้า-ออกทั้งบริษัท — ส่ง "YYYY-MM" (งวดปฏิทิน) หรือ {from,to}
 * (ช่วงที่กำหนดเอง เช่นรอบ 26 ชน 25)
 */
// opts.calendar — งวดแบบ "เดือนปฏิทิน" (1–สิ้นเดือน) แม้บริษัทจะจ่ายรอบ 26-25; ไม่ส่ง = งวดตามรอบจ่าย
export function fetchAttendanceExport(target: string | { from: string; to: string }, opts: { calendar?: boolean } = {}) {
  const qs =
    typeof target === 'string'
      ? `period=${encodeURIComponent(target)}${opts.calendar ? '&calendar=1' : ''}`
      : `from=${encodeURIComponent(target.from)}&to=${encodeURIComponent(target.to)}`;
  return apiFetch<AttendanceExport>(`/hr/attendance/export?${qs}`);
}

export function useRequestCancelApproved() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: 'leave' | 'ot'; record_id: string; reason: string }) =>
      apiFetch('/approvals/request-cancel', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['upcoming-approved'] }),
  });
}
export interface MyAttendanceDay {
  date: string;
  shift_code: string | null;
  shift_start: string | null;
  shift_end: string | null;
  /** fb#609 — เวลากะวันนี้มาจากใบเปลี่ยนเวลาที่อนุมัติแล้ว */
  shift_time_changed?: boolean;
  events: Record<string, { at: string; late_minutes: number; geofence_passed: boolean; selfie_path: string | null }>;
  /** fb#71 — days = เศษวันที่ลา · worked = ส่วนที่มาทำงาน (เครดิตเข้าวันทำงานเมื่อมีสแกนเข้า) ·
   *  scan_conflict = ลาเต็มวันแต่มีสแกนเข้างาน → HR ต้องตัดสิน (ปุ่ม "มาทำงานจริง") */
  /** leave_id = เลขใบ (LV-…) — fb#113 ปุ่มยกเลิกใบลาในแถววัน ส่งไป override-day action=void */
  leave: { id: string; leave_id: string | null; type: string; reason: string | null; days: number; worked: number; scan_conflict: boolean } | null;
  /** ใบ OT ที่อนุมัติของวันนั้น (วันเดียวมีได้หลายใบ) — ว่าง = ไม่มี OT */
  ot: OtOnDay[];
  /** ชั่วโมงรวมจากใบ OT ที่อนุมัติของวันนั้น */
  ot_hours: number;
  holiday: string | null;
  status: 'working' | 'short_work' | 'on_leave' | 'absent' | 'holiday' | 'future';
  /** fb#276 — หลักฐานใบลาที่อนุมัติแล้ว (รวมใบที่ยกเลิกทีหลัง) · มาเฉพาะเส้น HR /hr/attendance/employee */
  leave_proof?: LeaveProof[];
}
export interface LeaveProof {
  id: string;
  leave_id: string | null;
  type: string;
  reason: string | null;
  status: 'approved' | 'conditionally_approved' | 'cancelled';
  submitted_at: string | null;
  approvals: Array<{ level: number; name: string | null; at: string }>;
  /** ใบนำเข้าจากระบบเดิม (IMP-…) — ไม่มีผู้อนุมัติในระบบนี้ */
  imported_at: string | null;
  /** ลิงก์ไฟล์แนบที่เซ็นแล้ว — null เมื่อไม่มีไฟล์ หรือผู้ดูไม่ใช่ HR/OWNER */
  evidence_url: string | null;
  cancelled: { name: string | null; at: string | null; reason: string | null } | null;
}
export interface OtOnDay {
  /** id ของใบ OT — fb#83 HR แก้/ยกเลิกใบที่อนุมัติผิด */
  id: string;
  ot_type: string;
  start_time: string;
  end_time: string;
  hours: number;
  // ไม่มี `reason` โดยตั้งใจ — เป็นข้อความอิสระที่พนักงานพิมพ์ และเส้นนี้ MANAGER
  // เปิดดูของใครก็ได้ (ดูคอมเมนต์ใน workers/handlers/attendance-view.ts)
}
export interface MyAttendanceResp {
  range: { from: string; to: string };
  summary: {
    working_days: number; short_days: number; leave_days: number; absent_days: number; total_late_minutes: number;
    /** fb#71 — วันที่ "ลาเต็มวันแต่มีสแกนเข้างาน" (นับเป็นลาอยู่ จนกว่า HR จะยกเลิกใบลา) */
    leave_scan_days?: number;
    leave_scan_dates?: string[];
    /** ชั่วโมงรวมจากใบ OT ที่อนุมัติ — ปกติตรงกับที่ระบบใช้คิดเงิน (ดูข้อยกเว้นใน lib/attendance-live.ts) */
    ot_hours: number;
    ot_count: number;
  };
  days: MyAttendanceDay[];
  /** ช่วงจ้างงานตามประวัติ + วันที่มีสแกนแต่อยู่นอกช่วงนั้น (ระบบไม่นับ ไม่คิดเงิน) */
  employment?: {
    start_date: string | null;
    end_date: string | null;
    scan_days_outside: number;
    first_scan_outside: string | null;
    last_scan_outside: string | null;
  };
}
/** วันปิดรอบจ่ายของบริษัท (null = เดือนปฏิทิน) — หน้าประวัติเข้างานของพนักงานเปิดมาที่รอบจ่าย */
export function useMyPayCycle() {
  return useQuery({
    queryKey: ['me', 'pay-cycle'],
    queryFn: () => apiFetch<{ cutoff_day: number | null }>('/me/pay-cycle'),
    staleTime: 10 * 60_000,
  });
}

export function useMyAttendance(params: { from?: string; to?: string } = {}, enabled = true) {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['my-attendance', params],
    queryFn: () => apiFetch<MyAttendanceResp>(`/me/attendance/history${suffix}`),
    staleTime: 30_000,
    enabled,
  });
}

export interface EmployeeAttendanceResp extends MyAttendanceResp {
  employee: { emp_code: string; name: string };
}
// HR/Owner: ดูประวัติเข้างานของพนักงานคนหนึ่งทั้งช่วง (เลือกคน + ช่วงวันที่)
export function useEmployeeAttendance(empCode: string | null, params: { from?: string; to?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['employee-attendance', empCode, params],
    queryFn: () => apiFetch<EmployeeAttendanceResp>(`/hr/attendance/employee/${encodeURIComponent(empCode!)}${suffix}`),
    enabled: !!empCode,
    staleTime: 30_000,
  });
}

// === HR Attendance Today (TASK 22b) ===
export interface AttendanceTodayEmp {
  emp_code: string;
  first_name: string;
  last_name: string;
  nickname: string | null;
  department: string | null;
  position?: string | null;
}
export interface UnscannedRow extends AttendanceTodayEmp {
  shift_code: string | null;
  shift_start: string | null;
  shift_time_changed?: boolean; // fb#609
  minutes_late: number;
  last_nudged_at: string | null;
  can_nudge_now: boolean;
}
export interface ScannedRow extends AttendanceTodayEmp {
  morning_in_at: string;
  minutes_late: number;
  geofence_passed: boolean;
  approval_status: string;
  selfie_r2_path: string | null;
}
export interface OnLeaveRow extends AttendanceTodayEmp {
  leave_type: string;
  reason: string | null;
}
export interface DayOffRow extends AttendanceTodayEmp {
  scanned_in_at: string | null;
}
export interface ShortWorkRow extends AttendanceTodayEmp {
  shift_code: string | null;
  shift_end: string | null;
}
export interface AttendanceTodayResp {
  date: string;
  summary: { total_in_shift: number; scanned_morning_in: number; on_leave: number; unscanned: number; short_work: number; late?: number; day_off?: number };
  by_shift: Array<{
    shift_code: string;
    shift_label: string;
    start_time: string;
    end_time: string;
    in_shift: number;
    scanned: number;
    on_leave: number;
    unscanned: number;
    short_work: number;
    late?: number;
  }>;
  unscanned_list: UnscannedRow[];
  scanned_list: ScannedRow[];
  on_leave_list: OnLeaveRow[];
  short_work_list: ShortWorkRow[];
  day_off_list?: DayOffRow[];
}
export function useAttendanceToday(date?: string) {
  return useQuery({
    queryKey: ['hr-attendance-today', date],
    queryFn: () => apiFetch<AttendanceTodayResp>(`/hr/attendance/today${date ? `?date=${date}` : ''}`),
    refetchInterval: 60_000,
  });
}
export interface NudgeResult {
  sent: string[];
  throttled: Array<{ emp_code: string; reason: 'throttle' | 'quiet_hours' | 'no_line_id'; next_available_at?: string }>;
  failed: Array<{ emp_code: string; error: string }>;
}
export function useNudge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_codes: string[]; custom_message?: string; date?: string }) =>
      apiFetch<NudgeResult>('/hr/attendance/nudge', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-attendance-today'] }),
  });
}

export function useMyDecisions(params: { kind?: string; decision?: string; from?: string; to?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.kind) qs.set('kind', params.kind);
  if (params.decision) qs.set('decision', params.decision);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['my-decisions', params],
    queryFn: () => apiFetch<MyDecisionsResp>(`/me/decisions${suffix}`),
    staleTime: 30_000,
  });
}

// === Submit OT ===
export function useSubmitOT() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitOTRequest) =>
      apiFetch('/ot', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['history'] }),
  });
}

// === Check-in (4 events / day) ===
import type { CheckinKind, CheckinDayStatus } from '@payroll/shared';

export function useCheckinStatus() {
  return useQuery({
    queryKey: ['checkin-status'],
    queryFn: () =>
      apiFetch<CheckinDayStatus & {
        windows: Record<CheckinKind, string>;
        kinds: readonly CheckinKind[];
        shift: { name: string; start_time: string; end_time: string; time_override?: { orig_start_time: string; orig_end_time: string } | null } | null;
        attendance_exempt?: boolean;
        /** false = บริษัทปิดยืนยันเวลาเลิกงานเอง → ซ่อนเมนู ให้ขอรับรองเวลาผ่าน HR */
        self_confirm_clockout?: boolean;
      }>(
        '/checkin/status',
      ),
    refetchInterval: 60_000,
  });
}

export function useSubmitCheckin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      file: Blob;
      filename?: string;
      latitude: number;
      longitude: number;
      kind: CheckinKind;
    }) => {
      const selfiePath = await uploadFile(args.file, 'selfie', args.filename);
      return apiFetch<{
        date: string;
        kind: CheckinKind;
        geofence_passed: boolean;
        approval_status: string;
        shift_name: string | null;
        shift_start: string | null;
        late_minutes: number;
        unscheduled_work?: boolean;
        warning?: string | null;
      }>('/checkin', {
        method: 'POST',
        body: {
          selfie_path: selfiePath,
          latitude: args.latitude,
          longitude: args.longitude,
          kind: args.kind,
        } satisfies SubmitCheckinRequest,
        idempotencyKey: crypto.randomUUID(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checkin-status'] }),
  });
}

// === Approvals ===
export function usePendingApprovals() {
  return useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => apiFetch('/approvals/pending'),
    refetchInterval: 30_000,
  });
}

// fb#484 — ใบลาฉุกเฉิน (is_emergency) ที่รออนุมัติ กรองฝั่งเซิร์ฟเวอร์ (คิวรวมตัดที่ 100 ใบ)
export function useEmergencyApprovals(enabled = true) {
  return useQuery({
    queryKey: ['approvals', 'pending', 'emergency'],
    queryFn: () => apiFetch<{ leaves?: Array<{ is_emergency?: boolean } & Record<string, unknown>> }>('/approvals/pending?emergency=1'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled,
  });
}

export function useActOnApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApprovalActionRequest) =>
      apiFetch('/approvals/act', { method: 'POST', body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export interface MedicalCertificateCheck {
  decision: { flag: 'green' | 'yellow' | 'red'; message: string } | null;
}

/** Runs only while an HR/Owner has opened a leave with evidence. */
export function useMedicalCertificateCheck(leaveId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['approvals', 'medical-certificate', leaveId],
    queryFn: () => apiFetch<MedicalCertificateCheck>(`/approvals/medical-certificate/${encodeURIComponent(leaveId)}`),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

// === Slip ===
export function useSlipPeriods() {
  return useQuery({
    queryKey: ['slip-periods'],
    queryFn: () => apiFetch('/slip/periods'),
  });
}

export function useSlip(period: string | null) {
  return useQuery({
    queryKey: ['slip', period],
    queryFn: () => apiFetch(`/slip?period=${period}`),
    enabled: !!period,
  });
}

// === Geolocation ===
/**
 * Geolocation with hard timeout + graceful fallback.
 *
 * Why:
 *   - On iOS LINE LIFF the native geolocation Promise can hang forever
 *     (no resolve, no reject) — observed via the on-screen debug panel
 *     when "start" logged but "geo ok" never did. The browser's own
 *     `timeout` option isn't honoured reliably inside the LIFF webview.
 *   - Coarse position (enableHighAccuracy=false) returns in ~1s on a
 *     normal device, so coarse + a Promise.race fallback is the right
 *     escape hatch.
 *
 * Returns `{ latitude: 0, longitude: 0, available: false }` on timeout/
 * deny so the caller can still submit; the Worker treats a missing
 * fix as a failed geofence (approval_status='pending_review') instead
 * of blocking the check-in.
 */
export interface GeoResult {
  latitude: number;
  longitude: number;
  available: boolean;
}
export function useGeolocation() {
  return (timeoutMs = 6_000): Promise<GeoResult> => {
    if (!navigator.geolocation) {
      return Promise.resolve({ latitude: 0, longitude: 0, available: false });
    }
    const native = new Promise<GeoResult>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, available: true }),
        () => resolve({ latitude: 0, longitude: 0, available: false }),
        { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60_000 },
      );
    });
    const timeout = new Promise<GeoResult>((resolve) =>
      setTimeout(() => resolve({ latitude: 0, longitude: 0, available: false }), timeoutMs),
    );
    return Promise.race([native, timeout]);
  };
}

// === HR: Employees ===
export function useEmployees() {
  return useQuery({
    queryKey: ['hr', 'employees'],
    queryFn: () => apiFetch('/hr/employees'),
  });
}

export function useUpsertEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/hr/employees', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// (Re)issue a LINE pairing code for an existing employee — link later / re-link.
export function useReissuePairing() {
  return useMutation({
    mutationFn: (args: { emp_code: string; role?: string }) =>
      apiFetch<{ emp_code: string; pairing_code: string; expires_at: string }>(
        `/hr/employees/${encodeURIComponent(args.emp_code)}/pairing-code`,
        { method: 'POST', body: { role: args.role ?? 'EMPLOYEE' } },
      ),
  });
}

// เปลี่ยนรหัสพนักงาน (emp_code) — propagate ทั้งระบบ. OWNER + HR เต็มสิทธิ์เท่านั้น
// (gate ด้วย capability 'rename_emp_code'). กระทบลงเวลา/ลา/OT/payroll/สลิป ฯลฯ → ย้อนยาก.
export function useRenameEmpCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { old_emp_code: string; new_emp_code: string }) =>
      apiFetch<{ old_emp_code: string; new_emp_code: string }>(
        `/hr/employees/${encodeURIComponent(args.old_emp_code)}/rename-code`,
        { method: 'POST', body: { new_emp_code: args.new_emp_code } },
      ),
    // รหัสเปลี่ยน → ข้อมูลอ้างอิงเกือบทุกหน้าจอ stale, refresh ทั้งหมด
    onSuccess: () => qc.invalidateQueries(),
  });
}

// สายอนุมัติทั้งบริษัท (L1=หัวหน้า, L2/L3=คนที่กำหนด)
export interface ApprovalChainConfig {
  levels: number;
  l2_emp_code: string | null;
  l3_emp_code: string | null;
}
export function useApprovalChain() {
  return useQuery({
    queryKey: ['owner', 'approval-chain'],
    queryFn: () => apiFetch<ApprovalChainConfig>('/owner/approval-chain'),
  });
}
export function useSetApprovalChain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApprovalChainConfig) =>
      apiFetch<ApprovalChainConfig>('/owner/approval-chain', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner', 'approval-chain'] }),
  });
}

// ตั้งสิทธิ์ใช้งานระบบ (role) ให้พนักงานจากหน้ารายชื่อ (OWNER เท่านั้น)
export function useSetEmployeeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; role: 'HR' | 'MANAGER' | 'SUPERVISOR' | 'EMPLOYEE' }) =>
      apiFetch<{ emp_code: string; role: string; applied: boolean }>(
        `/hr/employees/${encodeURIComponent(args.emp_code)}/role`,
        { method: 'POST', body: { role: args.role } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// ทดสอบส่งแจ้งเตือนเข้า LINE ของพนักงานคนหนึ่ง (พิสูจน์ว่าเขาแอด OA แล้วรับ push ได้)
export function useTestNotify() {
  return useMutation({
    mutationFn: (empCode?: string) =>
      apiFetch<{ ok: boolean; message?: string; reason?: string; detail?: string }>('/owner/test-notify', {
        method: 'POST',
        body: empCode ? { emp_code: empCode } : {},
      }),
  });
}

// เช็คว่าพนักงานที่ผูก LINE แล้ว "แอด OA เป็นเพื่อน" หรือยัง (probe ทีละ batch).
// pair ≠ แอดเพื่อน → คนที่ไม่แอด push ไปก็ไม่ deliver. คืนสรุป + remaining.
// ไม่ invalidate ในตัว — caller วนเรียกหลาย batch แล้ว invalidate ครั้งเดียวตอนจบ.
export function useCheckFollowStatus() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ checked: number; followed: number; not_followed: number; unknown: number; remaining: number }>(
        '/hr/employees/check-follow',
        { method: 'POST', body: {} },
      ),
  });
}

// แจ้งปัญหา / ขอฟีเจอร์ (HR/Owner) — แนบ media (path จาก uploadFile) ได้
export interface FeedbackPayload {
  kind: 'bug' | 'feature';
  title: string;
  detail?: string;
  page_url?: string;
  context?: Record<string, unknown>;
  media?: Array<{ path: string; type?: string; name?: string }>;
}
export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (body: FeedbackPayload) =>
      apiFetch<{ id: string }>('/feedback', { method: 'POST', body }),
  });
}

// ข้อมูล OA + ลิงก์ "เพิ่มเพื่อน" สำหรับส่งให้พนักงานที่ยังไม่ได้แอดบอท
export function useOaInfo() {
  return useQuery({
    queryKey: ['hr', 'oa-info'],
    queryFn: () => apiFetch<{ basic_id: string | null; add_friend_url: string | null }>('/hr/oa-info'),
    staleTime: 60 * 60_000,
  });
}

// Unbind the employee's current LINE account.
export function useRevokeLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (empCode: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/revoke-line`, { method: 'POST', body: {} }),
    onSuccess: (_d, empCode) => {
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
      qc.invalidateQueries({ queryKey: ['hr', 'line-history', empCode] });
    },
  });
}

// Hard-delete a mistakenly-created employee (blocked once they have records).
export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (empCode: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// === HR: Allowances ===
export function useAllowances() {
  return useQuery({
    queryKey: ['hr', 'allowances'],
    queryFn: () => apiFetch('/hr/allowances'),
  });
}

export function useUpsertAllowance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/hr/allowances', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'allowances'] }),
  });
}

// === HR: Holidays ===
export function useHolidays(year?: number) {
  return useQuery({
    queryKey: ['hr', 'holidays', year ?? 'current'],
    queryFn: () => apiFetch(year ? `/hr/holidays?year=${year}` : '/hr/holidays'),
  });
}

export function useRecomputeCompHolidays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) =>
      apiFetch<{ year: number; compensatory: number }>(`/hr/holidays/recompute-comp?year=${year}`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'holidays'] }),
  });
}
export function useUpsertHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/hr/holidays', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'holidays'] }),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/holidays/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'holidays'] }),
  });
}

// === HR: Departments master (pp_departments — dropdown source) ===
export interface DepartmentOption {
  id: string;
  name: string;
}
export function useDepartments() {
  return useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: () => apiFetch<{ departments: DepartmentOption[] }>('/hr/departments').then((d) => d.departments),
    staleTime: 60_000,
  });
}

// === HR: Dept Holiday Override (เฟส 1 — วันหยุดนักขัตฤกษ์รายแผนก) ===
export interface DeptHolidayOverride {
  id: string;
  dept_name: string;
  date: string;
  kind: 'work' | 'off';
  name: string | null;
  source_date: string | null;
  created_at: string;
}
export interface DeptHolidayOverrideImpact {
  dept_name: string;
  dept_employee_count: number;
  scanned_count: number;
  would_become_absent_count: number;
  work_date: string;
  work_period: string;
  work_period_locked: boolean;
  off_date: string | null;
  off_period: string | null;
  off_period_locked: boolean | null;
  no_comp: boolean;
  note: string;
}
export interface DeptHolidayOverridePreview {
  requires_confirm: true;
  is_backdated: boolean;
  requires_backdated_confirm?: boolean;
  impact: DeptHolidayOverrideImpact;
}
export interface DeptHolidayOverrideSaved {
  work: DeptHolidayOverride;
  off: DeptHolidayOverride | null;
}
export type DeptHolidayOverrideBody = {
  dept_name: string;
  work_date: string;
  off_date?: string;
  off_name?: string;
  no_comp?: boolean;
  allow_backdated?: boolean;
  confirm?: boolean;
};

export function useDeptHolidayOverrides(year?: number) {
  return useQuery({
    queryKey: ['hr', 'dept-holiday-overrides', year ?? 'current'],
    queryFn: () =>
      apiFetch<DeptHolidayOverride[]>(year ? `/hr/dept-holiday-overrides?year=${year}` : '/hr/dept-holiday-overrides'),
  });
}

export function useUpsertDeptHolidayOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DeptHolidayOverrideBody) =>
      apiFetch<DeptHolidayOverridePreview | DeptHolidayOverrideSaved>('/hr/dept-holiday-overrides', { method: 'POST', body }),
    onSuccess: (data) => {
      if (!('requires_confirm' in data)) qc.invalidateQueries({ queryKey: ['hr', 'dept-holiday-overrides'] });
    },
  });
}

export function useDeleteDeptHolidayOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/dept-holiday-overrides/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'dept-holiday-overrides'] }),
  });
}

// === HR: Leave Quotas ===
export interface QuotaRow {
  emp_code: string;
  year: number;
  sick_quota: number;
  personal_quota: number;
  vacation_quota: number;
  package_id: string | null;
  package_name: string | null;
  prorate_factor: number;
  carried_days: number;
}
export function useQuotas(year?: number) {
  return useQuery({
    queryKey: ['hr', 'quotas', year],
    queryFn: () => apiFetch<QuotaRow[]>(`/hr/quotas${year ? `?year=${year}` : ''}`),
  });
}

export function useUpsertQuota() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/hr/quotas', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'quotas'] }),
  });
}

// === Pending Changes ===
export function usePendingChanges() {
  return useQuery({
    queryKey: ['pending-changes'],
    queryFn: () => apiFetch('/hr/pending-changes'),
    refetchInterval: 30_000,
  });
}

export function useDecidePendingChange() {
  const qc = useQueryClient();
  return useMutation({
    // backend รับ action: 'approve' | 'reject' (ยังรับ decision แบบเก่าได้ แต่ส่ง
    // ให้ตรงสัญญาไว้ดีกว่า — เคยส่งผิดคีย์แล้วกลายเป็น "ปฏิเสธ" เงียบ)
    // onboarding: true = คำขอเริ่มงานใหม่ ผ่านเส้น HR (fb#79 — HR ที่มี edit_employee อนุมัติเองได้)
    mutationFn: (body: { change_id: string; decision: 'approved' | 'rejected'; decision_note?: string; onboarding?: boolean }) =>
      apiFetch<{ change_id: string; status: string; emp_code?: string }>(body.onboarding ? '/hr/onboarding-requests/decide' : '/owner/pending-changes/decide', {
        method: 'POST',
        body: {
          change_id: body.change_id,
          action: body.decision === 'approved' ? 'approve' : 'reject',
          note: body.decision_note,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-changes'] });
      qc.invalidateQueries({ queryKey: ['onboarding-requests'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// === คำขอ "พนักงานเริ่มงานใหม่" (HR ยื่น → เจ้าของอนุมัติ) ===
export interface OnboardingRequest {
  change_id: string;
  status: 'pending' | 'approved' | 'rejected';
  decision_note: string | null;
  created_at: string;
  decided_at: string | null;
  name: string;
  department: string;
  position: string;
  start_date: string;
  note: string;
  has_nid: boolean;
}
export function useOnboardingRequests() {
  return useQuery({
    queryKey: ['onboarding-requests'],
    queryFn: () => apiFetch<OnboardingRequest[]>('/hr/onboarding-requests'),
    refetchInterval: 60_000,
  });
}
export function useSubmitOnboardingRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      first_name: string; last_name: string; national_id?: string;
      department?: string; position?: string; start_date?: string; note?: string;
    }) => apiFetch<{ change_id: string; status: string }>('/hr/onboarding-requests', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding-requests'] });
      qc.invalidateQueries({ queryKey: ['pending-changes'] });
    },
  });
}

// === Owner: Reconcile ===
export function useReconcile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string }) =>
      apiFetch('/owner/reconcile', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['escalations'] });
      // ภาพปัจจุบัน/ส่วนต่างกับตัวเลขคิดเงินเปลี่ยนทันทีหลังสรุปเวลา
      qc.invalidateQueries({ queryKey: ['attendance-live'] });
    },
  });
}

// === Owner: Payroll ===
// === ซ้อมทำเงินเดือน — คิดครบทุกกติกาของงวด แต่ไม่บันทึกอะไร (ไม่แตะสรุปเวลา/ร่างจริง) ===
export interface RehearsalRow {
  emp_code: string;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  department: string | null;
  position: string | null;
  employee_type: 'monthly' | 'daily' | 'hourly' | null;
  daily_rate: number | null;
  base_pay: number;
  working_days_paid: number | null;
  unpaid_leave_deduct: number;
  absent_deduct: number;
  ot_1_pay: number;
  ot_2_pay: number;
  ot_3_pay: number;
  additions_total: number;
  gross: number;
  tax_amount: number;
  sso_amount: number;
  pf_amount: number;
  /** fb#254 กองทุนสงเคราะห์ลูกจ้าง — ปิดตั้งค่า = 0 */
  welfare_fund_amount?: number;
  studentloan_amount: number;
  companyloan_amount: number;
  manual_deductions: number;
  deductions_total: number;
  net: number;
  pay_basis: PayBasis | null;
  line_items: {
    earnings?: Array<{ label: string; amount: number }>;
    deductions?: Array<{ label: string; amount: number }>;
    earnings_total?: number;
    deductions_total?: number;
  } | null;
  summary: (DaySummary & { late_days?: number; ot_1_hours?: number; ot_2_hours?: number; ot_3_hours?: number; has_unresolved?: boolean }) | null;
  scan_days?: number;
  offday_scan_days?: number;
  scan_leave_days?: number;
}
export interface RehearsalResult {
  period: string;
  period_start: string;
  period_end: string;
  computed_at: string;
  elapsed_ms: number;
  nothing_saved: true;
  projection_warning: string | null;
  employees_processed: number;
  escalations_found: number;
  unresolved_employees: number;
  totals: { gross_sum: number; deductions_sum: number; net_sum: number };
  warnings: Array<{ emp_code: string; type: string; label: string; prev_amount?: number }>;
  salary_only: boolean;
  rows: RehearsalRow[];
}
export interface RehearsalOptions {
  cutoff_day: number | null;
  calendar_month: boolean;
  current_period: string;
  today: string;
  periods: Array<{ period: string; from: string; to: string; label: string; future: boolean; current: boolean }>;
}
// งวด + ช่วงวันที่จากรอบจ่ายจริงของบริษัท (ตัวเดียวกับที่คิดเงิน) — จอห้ามคำนวณ cutoff เอง
export function useRehearsalOptions() {
  return useQuery({
    queryKey: ['payroll-rehearsal-options'],
    queryFn: () => apiFetch<RehearsalOptions>('/owner/payroll/rehearse/options'),
    staleTime: 60_000,
  });
}
export function useRehearsePayroll() {
  return useMutation({
    mutationFn: (body: { period: string; salary_only?: boolean }) =>
      apiFetch<RehearsalResult>('/owner/payroll/rehearse', { method: 'POST', body }),
    // ไม่ invalidate อะไร — ซ้อมไม่เปลี่ยนข้อมูลในระบบ
  });
}

export function useRunPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; force?: boolean; salary_only?: boolean }) =>
      apiFetch('/owner/payroll/run', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

export function useLockPayroll() {
  const qc = useQueryClient();
  return useMutation({
    // #hr-delegation Phase 3 — HR ที่ระดับ tier=pending จะได้ { queued:true, message }
    // (คำขอถูกส่งให้ owner) แทนการปิดงวดจริง. caller เช็ค queued เพื่อแสดงข้อความ.
    // fb#145 — acknowledge/ack_reason: ปิดทั้งที่มีเรื่องค้าง (soft-block 409) ต้องยืนยันพร้อมเหตุผล
    mutationFn: (body: { period: string; acknowledge?: boolean; ack_reason?: string }) =>
      apiFetch<{ queued?: boolean; message?: string }>('/owner/payroll/lock', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// === Pending actions (HR Delegation Phase 3) — คำขอ HR รอ owner อนุมัติ ===
export interface PendingAction {
  id: string;
  requested_by_name: string | null;
  action_type: string;
  params: Record<string, unknown>;
  summary: string;
  status: string;
  created_at: string;
}
export function usePendingActions() {
  return useQuery({
    queryKey: ['pending-actions'],
    queryFn: () => apiFetch<{ items: PendingAction[] }>('/owner/pending-actions').then((d) => d.items),
    staleTime: 30_000,
  });
}
export function useRejectPendingAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      apiFetch(`/owner/pending-actions/${id}/reject`, { method: 'POST', body: { note } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-actions'] }),
  });
}

// === Trial onboarding: pre-register employees (name+nid) ===
export interface RegisterResult {
  registered: number;
  skipped: Array<{ name: string; reason: string; existing?: { emp_code: string; name: string; status: string | null } }>;
  created: Array<{ emp_code: string; first_name: string; last_name: string; has_nid: boolean }>;
  tenant_code: string | null;
}
export interface RegisterRow {
  first_name: string;
  last_name: string;
  national_id?: string;
  department?: string;
  position?: string;
}
export function useRegisterEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: RegisterRow[]) =>
      apiFetch<RegisterResult>('/owner/employees/register', { method: 'POST', body: { rows } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
    },
  });
}

// === Owner: read base-salary history (decrypted) — current + past segments ===
export function useEmployeeSalary(empCode: string | null) {
  return useQuery({
    queryKey: ['employee-salary', empCode],
    enabled: !!empCode,
    queryFn: () =>
      apiFetch<{
        emp_code: string;
        base_salary: number | null;
        effective_from: string | null;
        history: Array<{ effective_from: string; effective_to: string | null; base_salary: number }>;
        employee_type: 'monthly' | 'daily' | 'hourly' | null;
        daily_rate: number | null;
      }>(`/owner/employees/${encodeURIComponent(empCode!)}/salary`),
  });
}

// === Owner: set base salary (relocated from the old onboard form) ===
export function useSetEmployeeSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; base_salary: number; effective_date?: string; wage_type?: 'monthly' | 'daily' | 'hourly' }) =>
      apiFetch(`/owner/employees/${encodeURIComponent(args.emp_code)}/salary`, {
        method: 'POST',
        body: { base_salary: args.base_salary, effective_date: args.effective_date, wage_type: args.wage_type ?? 'monthly' },
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee-salary', vars.emp_code] });
      qc.invalidateQueries({ queryKey: ['bulk-salary-prep'] }); // อัปเดตแบนเนอร์ "ยังไม่ตั้งเงินเดือน"
    },
  });
}

// === Owner: delete one salary entry (wrong/duplicate) ===
export function useDeleteEmployeeSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; effective_date: string }) =>
      apiFetch(`/owner/employees/${encodeURIComponent(args.emp_code)}/salary?effective_date=${encodeURIComponent(args.effective_date)}`, {
        method: 'DELETE',
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee-salary', vars.emp_code] });
      qc.invalidateQueries({ queryKey: ['bulk-salary-prep'] }); // ลบเงินเดือน → คนนี้กลับมาขึ้นแบนเนอร์
    },
  });
}

// === Owner: bulk base-salary helper (ตั้งฐานเงินเดือนหลายคนพร้อมกัน) ===
export interface SalaryRange { min: number; mid: number; max: number }
export interface BulkSalaryEmp {
  emp_code: string;
  name: string;
  position: string | null;
  department: string | null;
  employee_type: string | null;
  suggested: number | null;
  suggested_source: 'band' | 'structure' | null;
  band: SalaryRange | null;
  matched_title: string | null;
  match_quality: 'exact' | 'fuzzy' | null;
}
export interface SalaryReference extends SalaryRange {
  kind: 'position' | 'structure';
  id: string;
  title: string;
  level: number | null;
  department: string | null;
}
export interface BulkSalaryPrep {
  today: string;
  total_active: number;
  missing_count: number;
  employees: BulkSalaryEmp[];
  references: { positions: SalaryReference[]; structure: SalaryReference[] };
}
export function useBulkSalaryPrep(enabled: boolean) {
  return useQuery({
    queryKey: ['bulk-salary-prep'],
    queryFn: () => apiFetch<BulkSalaryPrep>('/owner/salary/bulk-prep'),
    enabled,
  });
}
export function useBulkSetSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { effective_date: string; items: Array<{ emp_code: string; base_salary: number }> }) =>
      apiFetch<{ ok: boolean; set: number; failed: Array<{ emp_code: string; reason: string }> }>(
        '/owner/salary/bulk', { method: 'POST', body: args },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bulk-salary-prep'] });
      qc.invalidateQueries({ queryKey: ['close-readiness'] });
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
    },
  });
}

// === HR permissions admin (HR Delegation Phase 4, OWNER-only) ===
export type CapGrant = 'allow' | 'pending' | 'deny';
export interface HrPermissionsConfig {
  default_tier: number;
  capabilities: Array<{ key: string; label: string }>;
  tiers: Record<string, Record<string, CapGrant>>;
  members: Array<{ user_id: string; emp_code: string | null; name: string; role: string; tier: number | null }>;
}
export function useHrPermissions() {
  return useQuery({
    queryKey: ['hr-permissions'],
    queryFn: () => apiFetch<HrPermissionsConfig>('/owner/hr-permissions'),
    staleTime: 60_000,
  });
}
export function useSaveHrPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { default_tier?: number; overrides?: Array<{ user_id: string; tier: number | null }> }) =>
      apiFetch('/owner/hr-permissions', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-permissions'] }),
  });
}

// === เบี้ยขยันขั้นบันได (diligence ladder) ===
export interface DiligenceLadder {
  id?: string;
  name?: string;
  active: boolean;
  steps: number[];
  start_tier: number;
  break_on_late: boolean;
  break_on_absent: boolean;
  break_leave_types: string[];
  reset_on_break: boolean;
  require_early_minutes: number;
  is_taxable: boolean;
  is_ssoable: boolean;
  is_pfable: boolean;
}
export function useDiligenceLadder() {
  return useQuery({
    queryKey: ['diligence-ladder'],
    queryFn: () => apiFetch<{ config: DiligenceLadder | null }>('/hr/diligence-ladder').then((d) => d.config),
  });
}
export function useSaveDiligenceLadder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DiligenceLadder) =>
      apiFetch<{ config: DiligenceLadder }>('/hr/diligence-ladder', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['diligence-ladder'] }),
  });
}

// === ทีมของฉัน (ผู้ใต้บังคับบัญชา) ===
export interface Subordinate {
  emp_code: string;
  name: string;
  nickname: string | null;
  position: string | null;
  department: string | null;
  status: string;
  phone: string | null;
}
export function useMySubordinates() {
  return useQuery({
    queryKey: ['my-subordinates'],
    queryFn: () => apiFetch<{ subordinates: Subordinate[] }>('/me/subordinates'),
    staleTime: 60_000,
  });
}

// รายชื่อ "หัวหน้า/ผู้จัดการ" สำหรับเลือกเป็นผู้อนุมัติ (ไม่รวมพนักงานทั่วไป)
export interface SupervisorCandidate {
  emp_code: string;
  name: string;
  position: string | null;
  department: string | null;
  is_lead: boolean;
}
export function useSupervisorCandidates() {
  return useQuery({
    queryKey: ['supervisor-candidates'],
    queryFn: () => apiFetch<{ candidates: SupervisorCandidate[] }>('/hr/supervisor-candidates').then((d) => d.candidates),
    staleTime: 60_000,
  });
}

// ตั้งหัวหน้างาน (ผู้อนุมัติลา/OT) ให้พนักงานหลายคนพร้อมกัน — supervisor_emp_code=null = ล้างผู้อนุมัติ
export function useBulkSetSupervisor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { supervisor_emp_code: string | null; emp_codes: string[] }) =>
      apiFetch<{ ok: boolean; set: number; skipped_self: number }>(
        '/hr/bulk-set-supervisor', { method: 'POST', body: args },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
      qc.invalidateQueries({ queryKey: ['blueprint'] });
    },
  });
}

// === My capabilities (HR delegation) — ใช้ซ่อน/แสดงเมนูที่ gate ด้วย requireCapability ===
export function useMyCapabilities() {
  return useQuery({
    queryKey: ['my-capabilities'],
    queryFn: () => apiFetch<{ capabilities: string[] }>('/me/capabilities').then((d) => d.capabilities),
    staleTime: 60_000,
  });
}

// === Salary analysis (วิเคราะห์เงินเดือน) — OWNER + HR เต็มสิทธิ์ (edit_salary) ===
export interface SalaryAnalysisRow {
  emp_code: string;
  name: string;
  department: string | null;
  position: string | null;
  base_salary: number;
  band: { min: number; mid: number; max: number } | null;
  compa_ratio: number | null;
  zone: 'below' | 'within' | 'above' | null;
}
export interface SalaryAnalysis {
  generated_at: string;
  summary: {
    headcount: number; total_base: number; avg: number; median: number; min: number; max: number;
    compa: { below: number; within: number; above: number; no_band: number };
  };
  by_department: Array<{ department: string; count: number; total: number; avg: number; min: number; max: number }>;
  rows: SalaryAnalysisRow[];
}
export function useSalaryAnalysis(enabled = true) {
  return useQuery({
    queryKey: ['salary-analysis'],
    queryFn: () => apiFetch<SalaryAnalysis>('/owner/salary/analysis'),
    enabled,
    staleTime: 30_000,
    retry: false, // 403 (สิทธิ์ไม่พอ) ไม่ต้อง retry
  });
}

// === Org structure (departments + positions, rich/hierarchy) — SoT (ADR 0003) ===
// PayPilot owns the org tables; these hit /hr/org/*. Eval reads them as overlay.
export interface OrgDepartment {
  id: string;
  tenant_id: string;
  name: string;
  parent_department_id: string | null;
  description: string | null;
  created_at?: string;
  updated_at?: string;
}
export interface OrgPosition {
  id: string;
  tenant_id: string;
  title: string;
  department_id: string | null;
  level: number;
  parent_position_id: string | null;
  qualifications?: Record<string, unknown>;
  holding_criteria?: Record<string, unknown>;
  headcount_target?: number;
  description: string | null;
  created_at?: string;
  updated_at?: string;
}
export function useOrgDepartments() {
  return useQuery({
    queryKey: ['org-departments'],
    queryFn: () => apiFetch<OrgDepartment[]>('/hr/org/departments'),
    staleTime: 60_000,
  });
}
export function useSaveOrgDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<OrgDepartment> & { id?: string }) =>
      id
        ? apiFetch<OrgDepartment>(`/hr/org/departments/${id}`, { method: 'PATCH', body })
        : apiFetch<OrgDepartment>('/hr/org/departments', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-departments'] }),
  });
}
export function useDeleteOrgDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/org/departments/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-departments'] }),
  });
}
export function useOrgPositions() {
  return useQuery({
    queryKey: ['org-positions'],
    queryFn: () => apiFetch<OrgPosition[]>('/hr/org/positions'),
    staleTime: 60_000,
  });
}
export function useSaveOrgPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<OrgPosition> & { id?: string }) =>
      id
        ? apiFetch<OrgPosition>(`/hr/org/positions/${id}`, { method: 'PATCH', body })
        : apiFetch<OrgPosition>('/hr/org/positions', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-positions'] }),
  });
}
export function useDeleteOrgPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/org/positions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-positions'] }),
  });
}

export function useSendSlips() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string }) =>
      apiFetch('/owner/payroll/send-slips', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// === Owner: 7-stage pipeline additions (Phase 1A.1) ===
export interface PayrollPreviewEmployee {
  emp_code: string;
  gross: number;
  deductions_total: number;
  net: number;
  prev_net: number | null;
  delta_pct: number | null;
  anomalies: string[];
}
export interface PayrollPreviewResult {
  period: string;
  status: 'previewed';
  totals: { gross_sum: number; deductions_sum: number; net_sum: number };
  prev_totals: { gross_sum: number; deductions_sum: number; net_sum: number };
  prev_period: string;
  employees: PayrollPreviewEmployee[];
}

export function usePreviewPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string }) =>
      apiFetch<PayrollPreviewResult>('/owner/payroll/preview', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

export function useApprovePayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string }) =>
      apiFetch<{ period: string; mode: 'owner' | 'hr_backup' | 'solo' }>(
        '/owner/payroll/approve',
        { method: 'POST', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

export function useRejectPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; reason: string }) =>
      apiFetch('/owner/payroll/reject', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// B6 — Owner-only undo of a locked/pdf_ready/sending/partial_sent/sent
// period. Reverts to 'approved' server-side; `warning` is non-null when
// slips already reached employees (must be re-sent after re-locking).
export interface PayrollUnlockResult {
  period: string;
  status: 'approved';
  rows: number;
  already_sent_count: number;
  warning: string | null;
}

export function useUnlockPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; reason: string }) =>
      apiFetch<PayrollUnlockResult>('/owner/payroll/unlock', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// === Deliveries tracking (Phase 1A.2) ===
export interface PayslipDelivery {
  id: string;
  emp_code: string;
  push_status: 'pending' | 'sent' | 'failed' | 'blocked';
  push_attempts: number;
  push_last_error: string | null;
  push_sent_at: string | null;
}

export function useDeliveries(period: string | null) {
  return useQuery({
    queryKey: ['deliveries', period],
    queryFn: () => apiFetch<PayslipDelivery[]>(`/owner/payroll/deliveries?period=${period}`),
    enabled: !!period,
    refetchInterval: (q) => {
      const rows = q.state.data as PayslipDelivery[] | undefined;
      // Poll while any delivery is in-flight or retryable
      return rows?.some((d) => d.push_status === 'pending' || d.push_status === 'failed')
        ? 5_000
        : false;
    },
  });
}

export function useRetryDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deliveryId: string) =>
      apiFetch<{ id: string; push_status: string; error?: string }>(
        `/owner/payroll/deliveries/${encodeURIComponent(deliveryId)}/retry`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliveries'] }),
  });
}

// === Phase 1B — Allowance master + eligibility ===
export interface AllowanceMasterRow {
  id: string;
  code: string;
  name_th: string;
  preset_type: string;
  is_taxable: boolean;
  is_ssoable: boolean;
  is_pfable: boolean;
  frequency: 'fixed_monthly' | 'variable';
  default_amount: number | null;
  default_rate: number | null;
  rate_unit: string | null;
  condition_type: 'perfect_attendance' | null;
  active: boolean;
}

export function useAllowanceMaster() {
  return useQuery({
    queryKey: ['allowance-master'],
    queryFn: () => apiFetch<AllowanceMasterRow[]>('/hr/allowance-master'),
  });
}
export function useUpsertAllowanceMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AllowanceMasterRow>) =>
      apiFetch<AllowanceMasterRow>('/hr/allowance-master', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allowance-master'] }),
  });
}
export function useDeleteAllowanceMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/allowance-master/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allowance-master'] }),
  });
}

export interface AllowanceEligibilityRow {
  id: string;
  emp_code: string;
  allowance_id: string;
  fixed_amount: number | null;
  rate: number | null;
  start_date: string;
  end_date: string | null;
  active: boolean;
}
export function useAllowanceEligibility(empCode: string | null) {
  return useQuery({
    queryKey: ['allowance-elig', empCode],
    queryFn: () =>
      apiFetch<AllowanceEligibilityRow[]>(
        `/hr/allowance-eligibility${empCode ? `?emp=${encodeURIComponent(empCode)}` : ''}`,
      ),
    enabled: empCode !== undefined,
  });
}
export function useAllowanceEligibilityByAllowance(allowanceId: string | null) {
  return useQuery({
    queryKey: ['allowance-elig-by', allowanceId],
    queryFn: () => apiFetch<AllowanceEligibilityRow[]>(`/hr/allowance-eligibility?allowance=${encodeURIComponent(allowanceId!)}`),
    enabled: !!allowanceId,
  });
}
export function useUpsertAllowanceEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AllowanceEligibilityRow>) =>
      apiFetch<AllowanceEligibilityRow>('/hr/allowance-eligibility', { method: 'POST', body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['allowance-elig'] }); qc.invalidateQueries({ queryKey: ['allowance-elig-by'] }); },
  });
}
export function useDeleteAllowanceEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/allowance-eligibility/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['allowance-elig'] }); qc.invalidateQueries({ queryKey: ['allowance-elig-by'] }); },
  });
}

// === Recurring deduction master + eligibility ===
export interface DeductionMasterRow {
  id: string;
  code: string;
  name_th: string;
  kind: 'statutory' | 'fixed';
  default_amount: number | null;
  note: string | null;
  active: boolean;
}
export function useDeductionMaster() {
  return useQuery({
    queryKey: ['deduction-master'],
    queryFn: () => apiFetch<DeductionMasterRow[]>('/hr/deduction-master'),
  });
}
export function useUpsertDeductionMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<DeductionMasterRow>) =>
      apiFetch<DeductionMasterRow>('/hr/deduction-master', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deduction-master'] }),
  });
}
export function useDeleteDeductionMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/deduction-master/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deduction-master'] }),
  });
}

export interface DeductionEligibilityRow {
  id: string;
  emp_code: string;
  deduction_id: string;
  amount: number | null;
  start_date: string;
  end_date: string | null;
  active: boolean;
}
export function useDeductionEligibility(params: { emp?: string | null; deduction?: string | null }) {
  const { emp, deduction } = params;
  return useQuery({
    queryKey: ['deduction-elig', emp ?? null, deduction ?? null],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (emp) qs.set('emp', emp);
      if (deduction) qs.set('deduction', deduction);
      const q = qs.toString();
      return apiFetch<DeductionEligibilityRow[]>(`/hr/deduction-eligibility${q ? `?${q}` : ''}`);
    },
    enabled: emp !== undefined || deduction !== undefined,
  });
}
export function useUpsertDeductionEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<DeductionEligibilityRow>) =>
      apiFetch<DeductionEligibilityRow>('/hr/deduction-eligibility', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deduction-elig'] }),
  });
}
export function useDeleteDeductionEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/deduction-eligibility/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deduction-elig'] }),
  });
}

// === Phase 1B — Data entry per period ===
export interface DataEntryRow {
  id: string;
  period: string;
  emp_code: string;
  entry_type: 'variable_allowance' | 'adjustment_add' | 'adjustment_deduct' | 'hours_worked';
  allowance_id: string | null;
  qty: number | null;
  adjustment_label: string | null;
  amount: number;
  adjustment_preset: string | null;
  notes: string | null;
  updated_at: string;
}

export function useDataEntries(period: string | null) {
  return useQuery({
    queryKey: ['data-entries', period],
    queryFn: () => apiFetch<DataEntryRow[]>(`/owner/payroll/data-entry?period=${period}`),
    enabled: !!period,
  });
}
export function useUpsertVariableAllowanceEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; emp_code: string; allowance_id: string; qty: number }) =>
      apiFetch('/owner/payroll/data-entry/variable-allowance', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-entries'] }),
  });
}
export type AdjustmentPreset = 'bonus' | 'wage_correction' | 'reimbursement';
export function useUpsertAdjustmentEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; emp_code: string; direction: 'add' | 'deduct'; label: string; amount: number; preset?: AdjustmentPreset; notes?: string }) =>
      apiFetch('/owner/payroll/data-entry/adjustment', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-entries'] }),
  });
}
export function useDeleteDataEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/owner/payroll/data-entry/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-entries'] }),
  });
}
// รายชั่วโมง: บันทึกชั่วโมงทำงานของพนักงานคนหนึ่งในงวด (แอดมินกรอกเอง)
export function useSetHoursWorked() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; emp_code: string; hours: number }) =>
      apiFetch('/owner/payroll/data-entry/hours-worked', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-entries'] }),
  });
}

// Per-employee payroll breakdown for the Run Draft detail drawer.
export interface PayrollRunDetail {
  emp_code: string;
  status: string;
  computed_at: string | null;
  gross: number;
  deductions_total: number;
  net: number;
  earnings: Array<{ label: string; amount: number }>;
  deductions: Array<{ label: string; amount: number }>;
  earnings_total: number;
  deductions_line_total: number;
  ytd: { gross?: number; tax?: number; sso?: number; pf?: number; net?: number } | null;
}
export function usePayrollRunDetail(period: string | null, empCode: string | null) {
  return useQuery({
    queryKey: ['payroll-run-detail', period, empCode],
    queryFn: () => apiFetch<PayrollRunDetail>(
      `/owner/payroll/run-detail?period=${encodeURIComponent(period!)}&emp_code=${encodeURIComponent(empCode!)}`,
    ),
    enabled: !!period && !!empCode,
  });
}

// === Owner: Audit Log ===
export function useAuditLog(limit = 50) {
  return useQuery({
    queryKey: ['audit-log', limit],
    queryFn: () => apiFetch(`/owner/audit-log?limit=${limit}`),
  });
}

// === Owner: Deductions ===
export function useDeductions() {
  return useQuery({
    queryKey: ['owner', 'deductions'],
    queryFn: () => apiFetch('/owner/deductions'),
  });
}

export function useUpsertDeduction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/owner/deductions', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner', 'deductions'] }),
  });
}

// === Owner: Dashboard summary (KPI counts) ===
export interface DashboardSummary {
  employees_active: number;
  pending_approvals: number;
  pending_leaves: number;
  pending_ots: number;
  today_checkins: number;
  today_leaves: number;
  pending_escalations: number;
  pending_changes: number;
}
export function useOwnerDashboardSummary() {
  return useQuery({
    queryKey: ['owner', 'dashboard-summary'],
    queryFn: () => apiFetch<DashboardSummary>('/owner/dashboard-summary'),
    refetchInterval: 60_000,
  });
}

// === Self profile (employee) ===
export interface ProfileResponse {
  profile: Record<string, unknown> & {
    emp_code: string;
    first_name: string | null;
    last_name: string | null;
    department: string | null;
    position: string | null;
    national_id: string | null;
    birth_date: string | null;
    phone: string | null;
    sso_number: string | null;
    bank: string | null;
    bank_account_masked: string | null;
    id_copy_path: string | null;
    bankbook_path: string | null;
    id_copy_url: string | null;
    bankbook_url: string | null;
    address_current: string | null;
    address_registered: string | null;
    emergency_name: string | null;
    emergency_relationship: string | null;
    emergency_phone: string | null;
  };
  completion_pct: number;
  missing_sections: string[];
}
export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => apiFetch<ProfileResponse>('/me/profile'),
  });
}
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, string | undefined>) =>
      apiFetch<{ completion_pct: number; saved: string[] }>('/me/profile', {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });
}

// === Owner: 7-day trend stats ===
export interface DailyStats { date: string; checkins: number; leaves: number; ots: number }
export function useLast7DaysStats() {
  return useQuery({
    queryKey: ['owner', 'stats', 'last7days'],
    queryFn: () => apiFetch<DailyStats[]>('/owner/stats/last7days'),
    refetchInterval: 5 * 60_000,
  });
}

// === Owner: Settings (key-value config) ===
export function useSettings() {
  return useQuery({
    queryKey: ['owner', 'settings'],
    queryFn: () => apiFetch<Record<string, string>>('/owner/settings'),
  });
}

/** สูตรเวลาชุดย่อย (หน้า เวลาทำงาน → ตั้งค่าเวลา) — ยิง /hr/time-settings
 *  ซึ่งเปิดถึงระดับ HR ต่างจาก /owner/settings ที่เป็นของเจ้าของเท่านั้น */
export function useTimeSettings() {
  return useQuery({
    queryKey: ['hr', 'time-settings'],
    queryFn: () => apiFetch<Record<string, string>>('/hr/time-settings'),
  });
}

// === HR: รอบจ่าย + ตัวเลือกคิดเงินรายชั่วโมง (config_pay) — หน้า ค่าจ้าง → รอบจ่ายเงินเดือน ===
export function usePayCycleSettings() {
  return useQuery({
    queryKey: ['hr', 'pay-cycle-settings'],
    queryFn: () => apiFetch<Record<string, string>>('/hr/pay-cycle-settings'),
  });
}

export function useUpdatePayCycleSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiFetch<{ updated: number }>('/hr/pay-cycle-settings', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'pay-cycle-settings'] });
      qc.invalidateQueries({ queryKey: ['hr', 'time-settings'] }); // CUTOFF_DAY โชว์ที่นั่นด้วย
      qc.invalidateQueries({ queryKey: ['owner', 'settings'] });
      qc.invalidateQueries({ queryKey: ['blueprint'] });
    },
  });
}

export function useUpdateTimeSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiFetch<{ updated: number }>('/hr/time-settings', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'time-settings'] });
      qc.invalidateQueries({ queryKey: ['owner', 'settings'] });
      qc.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiFetch<{ updated: number }>('/owner/settings', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner', 'settings'] });
      qc.invalidateQueries({ queryKey: ['hr', 'time-settings'] }); // กลุ่ม "สูตรเวลา" แชร์กับหน้า HR
      qc.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });
}

// Company identity used on official documents (payslip, 50ทวิ, …).
export interface CompanyProfile {
  company_name: string;
  company_address: string;
  tax_id: string;
  company_phone: string;
  signer_name: string;
  signer_title: string;
  /** Raw R2 path — round-trip with server on save (logo_path field). */
  logo_path: string;
  /** Signed URL for display in <img>. Empty when no logo set. */
  logo_url: string;
  /** รูปลายมือชื่อผู้มีอำนาจลงนาม (fb#640) — path R2 ส่งกลับตอนบันทึก */
  signature_path: string;
  /** Signed URL ของรูปลายมือชื่อ ว่าง = ยังไม่ตั้ง */
  signature_url: string;
}
export function useCompanyProfile() {
  return useQuery({
    queryKey: ['owner', 'company-profile'],
    queryFn: () => apiFetch<CompanyProfile>('/owner/company-profile'),
  });
}
export function useUpdateCompanyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CompanyProfile>) =>
      apiFetch<{ updated: number }>('/owner/company-profile', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner', 'company-profile'] });
      // การ์ดบริษัทในพิมพ์เขียวอ่านจาก useBlueprint — ไม่ล้างแล้ว HR บันทึกเสร็จกลับไปเห็นค่าเก่าอีก 2 นาที
      qc.invalidateQueries({ queryKey: ['blueprint'] });
    },
  });
}

// === Owner: Drive ===
export function useDriveStatus() {
  return useQuery({
    queryKey: ['drive-status'],
    queryFn: () => apiFetch('/owner/drive/status'),
    refetchInterval: 60_000,
  });
}

export function useConfigureDrive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { drive_root_id: string }) =>
      apiFetch('/owner/drive/configure', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drive-status'] }),
  });
}

export function useForceDriveSync() {
  return useMutation({
    mutationFn: () => apiFetch('/owner/drive/force-sync', { method: 'POST', body: {} }),
  });
}

export function useForceSheetSync() {
  return useMutation({
    mutationFn: () => apiFetch('/owner/sheet/force-sync', { method: 'POST', body: {} }),
  });
}

// === Owner: Onboard Employee ===
export function useOnboardEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ emp_code: string; pairing_code: string; expires_at: string }>(
        '/owner/onboard-employee',
        { method: 'POST', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// === Escalations ===
/** การ์ดกิจกรรมผิดปกติ "ของเดือนนี้" — ใช้บนแดชบอร์ด HR (ภาพรวมตอนนี้ ไม่ใช่ยอดสะสม) */
export function useEscalations() {
  const period = currentPeriod();
  return useQuery({
    queryKey: ['escalations', period],
    queryFn: () => apiFetch(`/escalations?period=${encodeURIComponent(period)}`),
  });
}

// === กิจกรรมผิดปกติ (หน้าจัดการ /hr/escalations) ===
export interface EscalationItem {
  esc_id: string;
  emp_code: string;
  date: string;
  type: string;
  detail: string | null;
  status: string;
  created_at: string;
}
export interface EscalationSummary {
  total: number;
  types: Array<{ type: string; count: number }>;
}
const ESCALATION_KEYS = [['escalations'], ['escalations-list'], ['escalations-summary']];
function invalidateEscalations(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ESCALATION_KEYS) qc.invalidateQueries({ queryKey: k });
}

export function useEscalationList(type: string | null, period?: string) {
  return useQuery({
    queryKey: ['escalations-list', type, period ?? null],
    queryFn: () =>
      apiFetch<EscalationItem[]>(
        `/escalations?limit=500${type ? `&type=${encodeURIComponent(type)}` : ''}${period ? `&period=${encodeURIComponent(period)}` : ''}`,
      ),
  });
}

export function useEscalationSummary(period?: string) {
  return useQuery({
    queryKey: ['escalations-summary', period ?? null],
    queryFn: () => apiFetch<EscalationSummary>(`/escalations/summary${period ? `?period=${encodeURIComponent(period)}` : ''}`),
  });
}

export function useResolveEscalation() {
  const qc = useQueryClient();
  return useMutation({
    // fb#438 decision ใช้ได้เฉพาะการ์ดเข้างานไม่ครบเวลา (time_shortfall) · 'waive' ต้องมี note
    // emp_code+date ของการ์ดที่เห็น — esc_id ถูกใช้ซ้ำได้หลังสรุปเวลาใหม่ เซิร์ฟเวอร์เทียบให้ตรงก่อนเขียน
    mutationFn: (args: { esc_id: string; emp_code: string; date: string; resolution: 'resolved' | 'ignored'; note?: string; decision?: 'deduct' | 'waive' }) =>
      apiFetch('/escalations/resolve', { method: 'POST', body: args }),
    onSuccess: () => invalidateEscalations(qc),
  });
}

export function useResolveAllEscalations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { type?: string; period?: string; resolution: 'resolved' | 'ignored'; note: string }) =>
      apiFetch<{ ok: boolean; count: number }>('/escalations/resolve-all', { method: 'POST', body: args }),
    onSuccess: () => invalidateEscalations(qc),
  });
}

// สั่งทับผลรายวัน: 'present' = ยืนยันมาทำงานปกติ (บันทึกสแกนแทน) · 'leave' = บันทึกเป็นลา ·
// 'worked' (fb#71) = วันนั้นมีใบลาแต่มาสแกนจริง → ยกเลิกใบลาของวันนั้น คืนสิทธิ์
export function useOverrideAttendanceDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; date: string; action: 'present' | 'leave' | 'worked' | 'void'; leave_type?: string; note?: string; leave_id?: string }) =>
      apiFetch<{ ok: boolean; cancelled?: number; employee_notified?: boolean }>('/escalations/override-day', { method: 'POST', body: args }),
    // settled (ไม่ใช่แค่ success): ถ้าใบถูกยกเลิกไปก่อนแล้ว (409) จอต้องโหลดสถานะจริงใหม่เช่นกัน
    onSettled: () => {
      invalidateEscalations(qc);
      qc.invalidateQueries({ queryKey: ['employee-attendance'] });
      qc.invalidateQueries({ queryKey: ['hr', 'leave-usage'] }); // fb#104 ยกเลิกจากหน้าสิทธิ์ลารายคน
    },
  });
}

// fb#83 — HR/OWNER แก้ OT ที่อนุมัติผิด: 'amend' หดช่วงเวลาภายในกรอบเดิม · 'void' ยกเลิกทั้งใบ
export function useAmendApprovedOt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; action: 'amend' | 'void'; start_time?: string; end_time?: string; reason: string }) =>
      apiFetch<{ ok: boolean; hours_before: number; hours_after: number; employee_notified?: boolean }>('/approvals/ot/amend', { method: 'POST', body: args }),
    // settled: ถ้าใบถูกแก้ไปก่อนแล้ว (409) จอต้องโหลดสถานะจริงใหม่เช่นกัน
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['employee-attendance'] });
    },
  });
}

// === Education + Work History (per employee) ===
export interface EducationRow {
  id: string;
  degree: string;
  institution: string;
  major: string | null;
  year_start: number | null;
  year_end: number | null;
  gpa: number | null;
  note: string | null;
}
export interface WorkHistoryRow {
  id: string;
  company: string;
  position: string;
  start_date: string | null;
  end_date: string | null;
  reason_for_leaving: string | null;
  note: string | null;
}

// Self-service (path /me/profile/...)
export function useOwnEducation() {
  return useQuery({
    queryKey: ['me', 'education'],
    queryFn: () => apiFetch<EducationRow[]>('/me/profile/education'),
  });
}
export function useUpsertOwnEducation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<EducationRow>) =>
      apiFetch<EducationRow>('/me/profile/education', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'education'] }),
  });
}
export function useDeleteOwnEducation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/me/profile/education/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'education'] }),
  });
}
export function useOwnWorkHistory() {
  return useQuery({
    queryKey: ['me', 'work-history'],
    queryFn: () => apiFetch<WorkHistoryRow[]>('/me/profile/work-history'),
  });
}
export function useUpsertOwnWorkHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<WorkHistoryRow>) =>
      apiFetch<WorkHistoryRow>('/me/profile/work-history', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'work-history'] }),
  });
}
export function useDeleteOwnWorkHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/me/profile/work-history/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'work-history'] }),
  });
}

// HR admin (path /hr/employees/:emp/...)
export function useEmployeeEducation(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'education', empCode],
    queryFn: () =>
      apiFetch<EducationRow[]>(`/hr/employees/${encodeURIComponent(empCode!)}/education`),
    enabled: !!empCode,
  });
}
export function useUpsertEmployeeEducation(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<EducationRow>) =>
      apiFetch<EducationRow>(`/hr/employees/${encodeURIComponent(empCode)}/education`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'education', empCode] }),
  });
}
export function useDeleteEmployeeEducation(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/education/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'education', empCode] }),
  });
}
export function useEmployeeWorkHistory(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'work-history', empCode],
    queryFn: () =>
      apiFetch<WorkHistoryRow[]>(`/hr/employees/${encodeURIComponent(empCode!)}/work-history`),
    enabled: !!empCode,
  });
}
export function useUpsertEmployeeWorkHistory(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<WorkHistoryRow>) =>
      apiFetch<WorkHistoryRow>(`/hr/employees/${encodeURIComponent(empCode)}/work-history`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'work-history', empCode] }),
  });
}
export function useDeleteEmployeeWorkHistory(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/work-history/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'work-history', empCode] }),
  });
}

// === Employee documents (N file attachments per employee) ===
export interface DocumentRow {
  id: string;
  label: string;
  r2_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  note: string | null;
  uploaded_at: string;
  url: string;        // worker-signed
}
export function useOwnDocuments() {
  return useQuery({
    queryKey: ['me', 'documents'],
    queryFn: () => apiFetch<DocumentRow[]>('/me/profile/documents'),
  });
}
export function useCreateOwnDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { label: string; r2_path: string; mime_type?: string; size_bytes?: number; note?: string }) =>
      apiFetch<DocumentRow>('/me/profile/documents', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'documents'] }),
  });
}
export function useDeleteOwnDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/me/profile/documents/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'documents'] }),
  });
}

export function useEmployeeDocuments(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'documents', empCode],
    queryFn: () =>
      apiFetch<DocumentRow[]>(`/hr/employees/${encodeURIComponent(empCode!)}/documents`),
    enabled: !!empCode,
  });
}
export function useCreateEmployeeDocument(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { label: string; r2_path: string; mime_type?: string; size_bytes?: number; note?: string }) =>
      apiFetch<DocumentRow>(`/hr/employees/${encodeURIComponent(empCode)}/documents`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'documents', empCode] }),
  });
}
export function useDeleteEmployeeDocument(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/documents/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'documents', empCode] }),
  });
}

// === หนังสือเตือน / ใบเตือน (disciplinary warnings) ===
export type WarningLevel = 'verbal' | 'written' | 'final';
export interface WarningRow {
  id: string;
  warning_date: string;
  level: WarningLevel;
  subject: string;
  detail: string | null;
  issued_by: string | null;
  acknowledged: boolean;
  r2_path: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  note: string | null;
  created_at: string;
  url: string | null;     // worker-signed (null = ไม่มีไฟล์แนบ)
}
export interface CreateWarningBody {
  warning_date: string;
  level: WarningLevel;
  subject: string;
  detail?: string;
  issued_by?: string;
  acknowledged?: boolean;
  r2_path?: string;
  mime_type?: string;
  size_bytes?: number;
  note?: string;
}
export function useEmployeeWarnings(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'warnings', empCode],
    queryFn: () =>
      apiFetch<WarningRow[]>(`/hr/employees/${encodeURIComponent(empCode!)}/warnings`),
    enabled: !!empCode,
  });
}
export function useCreateEmployeeWarning(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWarningBody) =>
      apiFetch<WarningRow>(`/hr/employees/${encodeURIComponent(empCode)}/warnings`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'warnings', empCode] }),
  });
}
export function useDeleteEmployeeWarning(empCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/employees/${encodeURIComponent(empCode)}/warnings/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'warnings', empCode] }),
  });
}

// === HR: Profile Followup (Day 7 / Day 14 pipeline) ===
export interface FollowupRow {
  emp_code: string;
  emp_name: string;
  profile_completion_pct: number;
  pair_completed_at: string;
  reminder_day7_sent_at: string | null;
  reminder_day14_sent_at: string | null;
  followup_status: 'active' | 'paused' | 'escalated' | 'completed';
  followup_completed_at: string | null;
  escalation_id: string | null;
  paused_at: string | null;
  pause_reason: string | null;
}

export function useFollowupList(status: 'active' | 'paused' | 'escalated' | 'completed' = 'active') {
  return useQuery({
    queryKey: ['hr', 'followup', status],
    queryFn: () => apiFetch<{ rows: FollowupRow[] }>(`/hr/followup/list?status=${status}`),
    refetchInterval: 60_000,
  });
}

export function usePauseFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; reason?: string }) =>
      apiFetch(`/hr/followup/${encodeURIComponent(args.emp_code)}/pause`, {
        method: 'POST',
        body: { reason: args.reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

export function useResumeFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string }) =>
      apiFetch(`/hr/followup/${encodeURIComponent(args.emp_code)}/resume`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

export function useSnoozeFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; days: number }) =>
      apiFetch(`/hr/followup/${encodeURIComponent(args.emp_code)}/snooze`, {
        method: 'POST',
        body: { days: args.days },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

export function useAdvanceFollowupClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emp_code: string; days: number }) =>
      apiFetch<{
        ok: boolean;
        emp_code: string;
        days_back: number;
        new_pair_completed_at: string;
      }>('/owner/test/advance-followup', { method: 'POST', body: args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

export function useRunFollowupNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ran: boolean; day7_sent: number; day14_sent: number; errors: number }>(
        '/owner/test/run-followup-now',
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'followup'] }),
  });
}

// === HR: Employee LINE pair history ===
export interface LineHistoryRow {
  id: string;
  line_user_id: string;
  started_at: string;
  ended_at: string | null;
  is_current: boolean;
  selfie_url: string | null;
  national_id_url: string | null;
  national_id_image_locked?: boolean;
}
export function useEmployeeLineHistory(empCode: string | null) {
  return useQuery({
    queryKey: ['hr', 'line-history', empCode],
    queryFn: () =>
      apiFetch<{ emp_code: string; history: LineHistoryRow[] }>(
        `/hr/employees/line-history?emp_code=${encodeURIComponent(empCode!)}`,
      ),
    enabled: !!empCode,
  });
}

// === Reveal sensitive data (เรื่อง 3b) — เปิดดูทีละค่า, server เขียน audit ===
export function useRevealNationalId() {
  return useMutation({
    mutationFn: (emp_code: string) =>
      apiFetch<{ emp_code: string; national_id: string }>('/hr/reveal/national-id', { method: 'POST', body: { emp_code } }),
  });
}
export function useRevealNationalIdImage() {
  return useMutation({
    mutationFn: (emp_code: string) =>
      apiFetch<{ emp_code: string; url: string }>('/hr/reveal/national-id-image', { method: 'POST', body: { emp_code } }),
  });
}
export function useRevealPayBand() {
  return useMutation({
    mutationFn: (bandId: string) =>
      apiFetch<{ id: string; min_salary: number; mid_salary: number; max_salary: number }>(
        `/hr/comp/bands/${encodeURIComponent(bandId)}/reveal`, { method: 'POST' },
      ),
  });
}

// === Payroll Runs (for a given period) ===
export function usePayrollRuns(period: string | null) {
  return useQuery({
    queryKey: ['payroll-runs', period],
    queryFn: () => apiFetch(`/owner/payroll/run?period=${period}`),
    enabled: !!period,
  });
}

// === Phase 1C — Commission ===

export interface CommissionTierRow {
  package_id: string;
  tier_order: number;
  min_amount: number;
  max_amount: number | null;
  rate_percent: number | null;
  rate_fixed: number | null;
  notes: string | null;
}

export interface CommissionPackageRow {
  id: string;
  name: string;
  description: string | null;
  basis: 'gross_sales' | 'units_sold' | 'gross_profit';
  min_threshold: number;
  cap: number | null;
  floor: number | null;
  active: boolean;
  tiers: CommissionTierRow[];
}

export function useCommissionPackages() {
  return useQuery({
    queryKey: ['commission', 'packages'],
    queryFn: () => apiFetch<CommissionPackageRow[]>('/hr/commission/packages'),
  });
}
export function useUpsertCommissionPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CommissionPackageRow> & { tiers: CommissionTierRow[] }) =>
      apiFetch<{ id: string }>('/hr/commission/packages', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'packages'] }),
  });
}
export function useDeleteCommissionPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/commission/packages/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'packages'] }),
  });
}

export interface CommissionEligibilityRow {
  id: string;
  emp_code: string;
  package_id: string;
  start_date: string;
  end_date: string | null;
  override_cap: number | null;
  override_floor: number | null;
  active: boolean;
}
export function useCommissionEligibility(empCode: string | null) {
  return useQuery({
    queryKey: ['commission', 'elig', empCode],
    queryFn: () =>
      apiFetch<CommissionEligibilityRow[]>(
        `/hr/commission/eligibility${empCode ? `?emp=${encodeURIComponent(empCode)}` : ''}`,
      ),
  });
}
export function useUpsertCommissionEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CommissionEligibilityRow>) =>
      apiFetch<CommissionEligibilityRow>('/hr/commission/eligibility', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'elig'] }),
  });
}
export function useDeleteCommissionEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/hr/commission/eligibility/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'elig'] }),
  });
}

export interface CommissionRunRow {
  id: string;
  period_start: string;
  period_end: string;
  status: 'entry' | 'calculated' | 'reviewed' | 'locked' | 'voided';
  feeds_payroll_period: string | null;
  locked_at: string | null;
}
export interface CommissionPerformanceRow {
  id: string;
  emp_code: string;
  gross_sales: number;
  units_sold: number;
  gross_profit: number;
  notes: string | null;
  package_id: string | null;
  calculated_amount: number | null;
  calculation_breakdown: unknown | null;
  calculated_at: string | null;
}

export function useCommissionRuns() {
  return useQuery({
    queryKey: ['commission', 'runs'],
    queryFn: () => apiFetch<CommissionRunRow[]>('/hr/commission/runs'),
  });
}
export function useCreateCommissionRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period_start: string; period_end: string }) =>
      apiFetch<{ id: string }>('/hr/commission/runs', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'runs'] }),
  });
}
export function useCommissionPerformance(runId: string | null) {
  return useQuery({
    queryKey: ['commission', 'perf', runId],
    queryFn: () =>
      apiFetch<CommissionPerformanceRow[]>(
        `/hr/commission/runs/${encodeURIComponent(runId!)}/performance`,
      ),
    enabled: !!runId,
  });
}
export function useUpsertCommissionPerformance(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_code: string; gross_sales?: number; units_sold?: number; gross_profit?: number; notes?: string }) =>
      apiFetch(`/hr/commission/runs/${encodeURIComponent(runId)}/performance`, { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission', 'perf', runId] });
      qc.invalidateQueries({ queryKey: ['commission', 'runs'] });
    },
  });
}
export function useCalculateCommission(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ calculated: number }>(`/hr/commission/runs/${encodeURIComponent(runId)}/calculate`, { method: 'POST', body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission', 'perf', runId] });
      qc.invalidateQueries({ queryKey: ['commission', 'runs'] });
    },
  });
}
export function useReviewCommission(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/hr/commission/runs/${encodeURIComponent(runId)}/review`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission', 'runs'] }),
  });
}
export function useLockCommission(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { feeds_payroll_period: string }) =>
      apiFetch<{ fed_entries: number }>(`/hr/commission/runs/${encodeURIComponent(runId)}/lock`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission'] }),
  });
}

// === Phase 2A — Year-end ===

export interface YearEndEmployeeRow {
  emp_code: string;
  emp_name: string;
  months_locked: number;
  gross_ytd: number;
  taxable_ytd: number;
  net_ytd: number;
  tax_ytd: number;
  sso_ytd: number;
  pf_ytd: number;
  ot_pay_ytd: number;
  last_updated: string;
}
export interface YearEndSummary {
  year: number;
  employees: YearEndEmployeeRow[];
  totals: { gross_sum: number; taxable_sum: number; tax_sum: number; sso_sum: number; pf_sum: number };
}
export function useYearEndSummary(year: number) {
  return useQuery({
    queryKey: ['year-end', year],
    queryFn: () => apiFetch<YearEndSummary>(`/owner/year-end?year=${year}`),
  });
}

export interface FiftyTawiData {
  year: number;
  no_data: boolean;
  employee: {
    emp_code: string;
    first_name: string;
    last_name: string;
    nickname?: string | null;
    national_id?: string | null;
    address_current?: string | null;
    sso_number?: string | null;
    position?: string | null;
    department?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  };
  tenant?: { company_name?: string; slug?: string } | null;
  totals?: {
    gross_ytd: number;
    taxable_ytd: number;
    net_ytd: number;
    tax_ytd: number;
    sso_ytd: number;
    pf_ytd: number;
    ot_pay_ytd: number;
    last_updated: string;
  };
}
export function useFiftyTawi(year: number, empCode: string | null) {
  return useQuery({
    queryKey: ['50tawi', year, empCode],
    queryFn: () => apiFetch<FiftyTawiData>(`/owner/year-end/50tawi?year=${year}&emp=${encodeURIComponent(empCode!)}`),
    enabled: !!empCode,
  });
}
export function useRecomputeYtd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) =>
      apiFetch('/owner/year-end/recompute-ytd', { method: 'POST', body: { year } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['year-end'] }),
  });
}

// === Phase 2B — Termination ===
export interface TerminationPreview {
  emp_code: string;
  emp_name: string;
  effective_date: string;
  reason_code: 'resigned' | 'terminated' | 'retired' | 'contract_end';
  tenure_days: number;
  tenure_years: number;
  daily_rate: number;
  severance_days: number;
  severance_amount: number;
  accrued_leave_days: number;
  accrued_leave_amount: number;
  notice_pay: number;
  total_payout: number;
}

export function usePreviewTermination() {
  return useMutation({
    mutationFn: (body: {
      emp_code: string;
      effective_date: string;
      reason_code: string;
      notice_shortfall_days?: number;
      accrued_leave_days_override?: number;
    }) => apiFetch<TerminationPreview>('/hr/termination/preview', { method: 'POST', body }),
  });
}
export function useCommitTermination() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      emp_code: string;
      effective_date: string;
      reason_code: string;
      notice_shortfall_days?: number;
      accrued_leave_days_override?: number;
      reason_note?: string;
      feeds_payroll_period: string;
    }) => apiFetch('/hr/termination/commit', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'employees'] }),
  });
}

// === Phase 3 — Shift master ===
export interface ShiftMasterRow {
  id: string;
  code: string;
  name: string;
  start_time: string;
  end_time: string;
  crosses_midnight: boolean;
  break_minutes: number;
  paid_hours: number;
  night_premium_type: 'none' | 'percent' | 'flat';
  night_premium_value: number;
  night_window_start: string;
  night_window_end: string;
  default_weekdays: number[];
  active: boolean;
  flexible?: boolean;
  require_clock_out?: boolean;
  flex_check_in_by?: string | null;
}
export function useShiftMaster() {
  return useQuery({ queryKey: ['shift', 'master'], queryFn: () => apiFetch<ShiftMasterRow[]>('/hr/shift/master') });
}
export function useUpsertShiftMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ShiftMasterRow>) => apiFetch<ShiftMasterRow>('/hr/shift/master', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'master'] }),
  });
}
export function useDeleteShiftMaster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; deleted: boolean; deactivated: boolean }>(
        `/hr/shift/master/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      ),
    // Hard delete cascades shift_assignment → refresh the whole shift tree.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}

export interface ShiftAssignmentRow {
  id: string;
  emp_code: string;
  shift_id: string;
  weekdays: number[];
  start_date: string;
  end_date: string | null;
  active: boolean;
  created_at?: string | null;
}
export function useShiftAssignments(empCode: string | null) {
  return useQuery({
    queryKey: ['shift', 'assign', empCode],
    queryFn: () => apiFetch<ShiftAssignmentRow[]>(`/hr/shift/assignment${empCode ? `?emp=${encodeURIComponent(empCode)}` : ''}`),
  });
}
export function useShiftAssignmentsByShift(shiftId: string | null) {
  return useQuery({
    queryKey: ['shift', 'assign-by-shift', shiftId],
    queryFn: () => apiFetch<ShiftAssignmentRow[]>(`/hr/shift/assignment?shift=${encodeURIComponent(shiftId!)}`),
    enabled: !!shiftId,
  });
}
export function useUpsertShiftAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ShiftAssignmentRow>) => apiFetch<ShiftAssignmentRow>('/hr/shift/assignment', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}
export function useDeleteShiftAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/hr/shift/assignment/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}

// === วันหยุดหมุนเวียน / นำเข้าตารางเวร (P2/P3) ===
export interface RosterImportRow {
  key: string;
  matched: boolean;
  emp_code: string | null;
  name: string | null;
  ambiguous: boolean;
  days: Array<{ date: string; raw: string; kind: 'shift' | 'off' | 'ignore' | 'unknown'; label?: string }>;
}
export interface RosterImportDiagnostics {
  employees_matched: number;
  unmatched_keys: string[];
  unknown_codes: Array<{ raw: string; count: number }>;
  week_warnings: Array<{ emp_code: string; name: string; weeks?: string[] }>;
  work_cells: number;
  off_cells: number;
  unknown_cells: number;
  writable_entries: number;
  roster_mode_set?: number;
  leave_conflicts?: Array<{ emp_code: string; date: string }>;
}
export interface RosterImportResult {
  period: string;
  rows: RosterImportRow[];
  entries: Array<{ emp_code: string; date: string; shift_id: string | null }>;
  diagnostics: RosterImportDiagnostics;
}
export interface RosterImportPayload {
  period: string;
  rows: Array<{ key: string; cells: Record<string, string> }>;
}

export function useRosterImportPreview() {
  return useMutation({
    mutationFn: (body: RosterImportPayload) =>
      apiFetch<RosterImportResult>('/hr/shift/roster/import/preview', { method: 'POST', body }),
  });
}
export function useRosterImportCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RosterImportPayload) =>
      apiFetch<{ written: number; diagnostics: RosterImportDiagnostics }>('/hr/shift/roster/import/commit', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}
export function useCodeAlias() {
  return useQuery({
    queryKey: ['shift', 'code-alias'],
    queryFn: () => apiFetch<{ aliases: Record<string, string> }>('/hr/shift/code-alias'),
  });
}
export function useSaveCodeAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aliases: Record<string, string>) =>
      apiFetch<{ aliases: Record<string, string> }>('/hr/shift/code-alias', { method: 'POST', body: { aliases } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'code-alias'] }),
  });
}
export function useCopyPrevMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; overwrite: boolean }) =>
      apiFetch<{ from: string; to: string; rows: number }>('/hr/shift/roster/copy-prev-month', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}

export interface WorkScheduleSettings {
  weekend_days: number[];
  workdays_per_week: number;
  hours_per_workday: number;
}
export function useWorkSchedule() {
  return useQuery({
    queryKey: ['hr', 'work-schedule'],
    queryFn: () => apiFetch<WorkScheduleSettings>('/hr/work-schedule'),
  });
}

export interface RosterRow {
  id: string;
  emp_code: string;
  date: string;
  shift_id: string | null;
  source: 'manual' | 'swap' | 'assignment';
  note: string | null;
}
export function useRoster(from: string, to: string) {
  return useQuery({
    queryKey: ['shift', 'roster', from, to],
    queryFn: () => apiFetch<RosterRow[]>(`/hr/shift/roster?from=${from}&to=${to}`),
    enabled: !!from && !!to,
  });
}
export function useUpsertRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_code: string; date: string; shift_id: string | null; note?: string }) =>
      apiFetch('/hr/shift/roster', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'roster'] }),
  });
}

export interface SwapRow {
  id: string;
  date: string;
  requester_emp_code: string;
  requester_shift_id: string | null;
  target_emp_code: string;
  target_shift_id: string | null;
  reason: string | null;
  status: 'awaiting_target' | 'pending' | 'approved' | 'rejected' | 'declined' | 'cancelled';
  target_responded_at?: string | null;
  decided_at: string | null;
  decision_note: string | null;
}
export function useSwapRequests(status?: string) {
  return useQuery({
    queryKey: ['shift', 'swap', status ?? 'all'],
    queryFn: () => apiFetch<SwapRow[]>(`/shift/swap${status ? `?status=${status}` : ''}`),
    refetchInterval: 30_000,
  });
}
export function useCreateSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; requester_emp_code: string; target_emp_code: string; reason?: string }) =>
      apiFetch('/shift/swap', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'swap'] }),
  });
}
export function useDecideSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected'; decision_note?: string }) =>
      apiFetch(`/shift/swap/${encodeURIComponent(args.id)}/decide`, {
        method: 'POST',
        body: { decision: args.decision, decision_note: args.decision_note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift', 'swap'] });
      qc.invalidateQueries({ queryKey: ['approvals', 'pending'] });
    },
  });
}
// สลับเวร — มุมพนักงาน (ดูคำขอที่เกี่ยวกับฉัน + ตอบรับ + เลือกเพื่อน)
export function useMySwaps() {
  return useQuery({
    queryKey: ['shift', 'swap', 'mine'],
    queryFn: () => apiFetch<SwapRow[]>('/shift/swap/mine'),
    refetchInterval: 30_000,
  });
}
export function useRespondSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; accept: boolean }) =>
      apiFetch(`/shift/swap/${encodeURIComponent(args.id)}/respond`, { method: 'POST', body: { accept: args.accept } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'swap'] }),
  });
}
export interface CoworkerRow { emp_code: string; name: string; nickname: string | null }
export function useCoworkers() {
  return useQuery({ queryKey: ['me', 'coworkers'], queryFn: () => apiFetch<CoworkerRow[]>('/me/coworkers') });
}

// === ขอย้ายวันหยุด (shift change request) ===
export interface ShiftChangeRow {
  id: string;
  original_date: string;
  target_date: string;
  original_shift_id: string | null;
  target_shift_id: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  is_urgent?: boolean;
  submitted_at: string;
  decided_at: string | null;
  rejection_reason: string | null;
}
export interface ShiftOption { id: string; code: string; name: string; start_time: string; end_time: string }

/** กะที่ใช้งานอยู่ (พนักงานทุกคนเรียกได้) — ใช้ในฟอร์มขอย้ายวันหยุดให้เลือกกะวันกลับมาทำงาน. */
export function useShiftOptions() {
  return useQuery({
    queryKey: ['me', 'shift-options'],
    queryFn: () => apiFetch<ShiftOption[]>('/me/shift-options'),
    staleTime: 5 * 60_000,
  });
}
/** โหมดตารางเวลาของฉัน — ใช้ซ่อนเมนู "ขอย้ายวันหยุด" เมื่อไม่ใช่โหมด roster
 *  (company_weekend ระบบวันหยุดไม่ได้อ่าน roster เลย ย้ายแบบนี้ไม่มีความหมาย). */
export function useMyScheduleMode() {
  return useQuery({
    queryKey: ['me', 'schedule-mode'],
    queryFn: () => apiFetch<{ schedule_mode: 'company_weekend' | 'roster' | 'on_call' | null }>('/me/schedule-mode'),
    staleTime: 5 * 60_000,
  });
}
/** กะของฉันในวันที่ระบุ (self) — ใช้พรีฟิลกะ dropdown ในฟอร์มขอย้ายวันหยุด
 *  ด้วยกะจริงของ target_date แทนตัวเลือก "อัตโนมัติ" ลอย ๆ. */
export function useMyShiftResolve(date: string | undefined) {
  return useQuery({
    queryKey: ['me', 'shift-resolve', date],
    queryFn: () => apiFetch<{ shift: { shift_id: string; code: string; name: string } | null }>(
      `/me/shift-resolve?date=${encodeURIComponent(date!)}`,
    ),
    enabled: !!date,
    staleTime: 60_000,
  });
}
export function useMyShiftChanges() {
  return useQuery({
    queryKey: ['me', 'shift-change'],
    queryFn: () => apiFetch<ShiftChangeRow[]>('/me/shift-change-requests'),
    refetchInterval: 30_000,
  });
}
export function useCreateShiftChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { original_date: string; target_date: string; original_shift_id?: string; reason?: string }) =>
      apiFetch('/me/shift-change-request', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'shift-change'] }),
  });
}
export function useDecideShiftChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected'; rejection_reason?: string }) =>
      apiFetch(`/shift-change/${encodeURIComponent(args.id)}/decide`, {
        method: 'POST',
        body: { decision: args.decision, rejection_reason: args.rejection_reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

// === fb#609 ขอเปลี่ยนเวลาเข้า-ออกงานรายวัน (กะเดิม ชั่วโมงเท่าเดิม) ===
export interface ShiftTimeRow {
  id: string;
  emp_code?: string;
  emp_name?: string;
  work_date: string;
  orig_start_time: string;
  orig_end_time: string;
  new_start_time: string;
  new_end_time: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  submitted_at?: string;
  decided_at?: string | null;
  rejection_reason?: string | null;
}
/** กะของฉันวันนั้นพร้อมเวลา (ใช้คำนวณเวลาเลิกให้ดูก่อนส่ง) — queryKey แยกจากฟอร์มย้ายวันหยุด */
export function useMyShiftForTime(date: string | undefined) {
  return useQuery({
    queryKey: ['me', 'shift-resolve', date, 'time'],
    queryFn: () => apiFetch<{ shift: { shift_id: string; code: string; name: string; start_time: string; end_time: string; crosses_midnight: boolean; flexible: boolean; time_override?: { orig_start_time: string; orig_end_time: string } } | null }>(
      `/me/shift-resolve?date=${encodeURIComponent(date!)}`,
    ),
    enabled: !!date,
    staleTime: 60_000,
  });
}
export function useMyShiftTimes() {
  return useQuery({
    queryKey: ['me', 'shift-time'],
    queryFn: () => apiFetch<ShiftTimeRow[]>('/me/shift-time-requests'),
    refetchInterval: 30_000,
  });
}
export function useCreateShiftTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { work_date: string; new_start_time: string; reason: string }) =>
      apiFetch<{ id: string; new_end_time: string }>('/me/shift-time-request', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'shift-time'] }),
  });
}
export function useCancelShiftTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; reason?: string }) =>
      apiFetch(`/shift-time/${encodeURIComponent(args.id)}/cancel`, { method: 'POST', body: { reason: args.reason } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'shift-time'] });
      qc.invalidateQueries({ queryKey: ['shift-time', 'approved'] });
    },
  });
}
export function useDecideShiftTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected'; rejection_reason?: string }) =>
      apiFetch(`/shift-time/${encodeURIComponent(args.id)}/decide`, {
        method: 'POST',
        body: { decision: args.decision, rejection_reason: args.rejection_reason },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['shift-time', 'approved'] });
    },
  });
}
/** HR/เจ้าของ: ใบที่อนุมัติแล้ว (ยกเลิกได้ถ้างวดยังไม่ล็อก) */
export function useApprovedShiftTimes(enabled: boolean) {
  return useQuery({
    queryKey: ['shift-time', 'approved'],
    queryFn: () => apiFetch<ShiftTimeRow[]>('/shift-time-requests/approved'),
    enabled,
    staleTime: 15_000,
  });
}

// === รับรองเวลาเข้างาน (พนักงานลืมสแกน ขอแก้เวลาย้อนหลัง · HR อนุมัติ) ===
export interface AttendanceCorrectionRow {
  id: string;
  emp_code?: string;
  emp_name?: string;
  date: string;
  kind: 'morning_in' | 'evening_out';
  requested_time: string;
  reason: string;
  // fb#429: pending_supervisor = ขั้น 1 รอหัวหน้า · pending = ขั้น 2 รอ HR
  status: 'pending_supervisor' | 'pending' | 'approved' | 'rejected' | 'cancelled';
  submitted_at?: string;
  supervisor_decided_at?: string | null;
  decided_at?: string | null;
  rejection_reason?: string | null;
  evidence_url?: string | null;
}
export function useMyAttendanceCorrections() {
  return useQuery({
    queryKey: ['me', 'attendance-correction'],
    queryFn: () => apiFetch<AttendanceCorrectionRow[]>('/me/attendance-corrections'),
    refetchInterval: 30_000,
  });
}
export function useCreateAttendanceCorrection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; kind: 'morning_in' | 'evening_out'; requested_time: string; reason: string; evidence_path?: string }) =>
      apiFetch('/me/attendance-correction', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'attendance-correction'] }),
  });
}
export function usePendingAttendanceCorrections(enabled = true) {
  // fb#429: คิวนี้ต่างกันตามคน (HR เห็นทั้งบริษัท · หัวหน้าเห็นแค่ทีม) — ผูก cache กับบัญชี กันเห็นของบัญชีก่อนหน้า
  const uid = useAuthStore((s) => s.profile?.user_id ?? '');
  return useQuery({
    queryKey: ['hr', 'attendance-correction', 'pending', uid],
    queryFn: () => apiFetch<AttendanceCorrectionRow[]>('/hr/attendance-corrections'),
    enabled,
    refetchInterval: 30_000,
  });
}
export function useDecideAttendanceCorrection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected'; rejection_reason?: string; expected_status: 'pending_supervisor' | 'pending' }) =>
      apiFetch(`/attendance-correction/${encodeURIComponent(args.id)}/decide`, {
        method: 'POST',
        body: { decision: args.decision, rejection_reason: args.rejection_reason, expected_status: args.expected_status },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'attendance-correction'] });
      qc.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}
// HR ลงเวลาย้อนหลังให้พนักงานเอง (พนักงานลืมสแกนและไม่ได้ยื่นคำขอ)
export interface ManualAttendanceEntryInput {
  emp_code: string;
  date: string;
  kind: 'morning_in' | 'lunch_out' | 'lunch_in' | 'evening_out';
  time: string;
  reason: string;
}
export function useManualAttendanceEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ManualAttendanceEntryInput) =>
      apiFetch<{ replaced_existing: boolean; minutes_late: number; label: string }>('/hr/attendance/manual-entry', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employee-attendance'] });
    },
  });
}

// === แพทเทิร์นหมุนเวรอัตโนมัติ ===
export interface RotationPatternRow {
  id: string;
  code: string;
  name: string;
  sequence: (string | null)[];
  cycle_length: number;
  active: boolean;
}
export function useRotationPatterns() {
  return useQuery({ queryKey: ['shift', 'rotation'], queryFn: () => apiFetch<RotationPatternRow[]>('/hr/shift/rotation') });
}
export function useUpsertRotationPattern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id?: string; code: string; name: string; sequence: (string | null)[]; active: boolean }) =>
      apiFetch<RotationPatternRow>('/hr/shift/rotation', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'rotation'] }),
  });
}
export function useDeleteRotationPattern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/hr/shift/rotation/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}
export function useGenerateRotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) =>
      apiFetch<{ period: string; written: number; employees: number; skipped_manual: number }>('/hr/shift/rotation/generate', { method: 'POST', body: { period } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift'] }),
  });
}

// === Tickets (ร้องเรียน / แจ้งเหตุ / ร้องทุกข์ + คำขอแก้ย้อนหลัง) ===
export type TicketCategory =
  | 'complaint' | 'report' | 'grievance'
  | 'leave_backdate' | 'leave_type_change' | 'leave_edit_locked' | 'schedule_change';
export type TicketStatus =
  | 'open' | 'under_review' | 'escalated' | 'approved' | 'rejected' | 'resolved';

export interface Ticket {
  id: string;
  emp_code: string;
  category: TicketCategory;
  subject: string;
  detail: string;
  ref_note: string | null;
  status: TicketStatus;
  hr_note: string | null;
  hr_at: string | null;
  decision_note: string | null;
  decided_role: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useTickets(opts?: { status?: string; mine?: boolean }) {
  const qs = new URLSearchParams();
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.mine) qs.set('mine', '1');
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['tickets', opts?.status ?? 'all', opts?.mine ? 'mine' : 'all'],
    queryFn: () => apiFetch<Ticket[]>(`/tickets${suffix}`),
  });
}
export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { category: TicketCategory; subject: string; detail: string; ref_note?: string }) =>
      apiFetch('/tickets', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}
export function useTriageTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; action: 'review' | 'escalate' | 'resolve'; hr_note?: string }) =>
      apiFetch('/tickets/triage', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}
export function useDecideTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; decision: 'approved' | 'rejected'; decision_note: string }) =>
      apiFetch('/tickets/decide', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

// === เรื่อง 3 — self-confirm clock-out ===
export interface MissingClockOut { date: string; detail: string | null; status: string }
export function useMissingClockOut() {
  return useQuery({
    queryKey: ['missing-clockout'],
    queryFn: () => apiFetch<MissingClockOut[]>('/checkin/missing-clockout'),
  });
}
export function useConfirmClockOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; mode: 'shift_end' | 'actual'; actual_time?: string; reason?: string }) =>
      apiFetch('/checkin/confirm-clockout', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['missing-clockout'] });
      qc.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });
}

// === เรื่อง 1 — emergency leave HR review ===
export interface EmergencyPending {
  leave_id: string;
  emp_code: string;
  date: string;
  reason: string | null;
  conditional_evidence_due: string | null;
  created_at: string;
  emp_name?: string;
  department?: string | null;
  position?: string | null;
}
export function useEmergencyPending() {
  return useQuery({
    queryKey: ['emergency-pending'],
    queryFn: () => apiFetch<EmergencyPending[]>('/leave/emergency/pending'),
    refetchInterval: 30_000,
  });
}
export function useDecideEmergencyLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { leave_id: string; decision: 'paid' | 'unpaid' | 'absent'; reason: string; deduct_quota: boolean }) =>
      apiFetch('/leave/emergency/decide', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emergency-pending'] }),
  });
}

// === Sidebar badge counts (pending items per menu item) ===
// Single hook so AdminLayout can show "(N)" next to each approval menu without
// every page bolting on its own fetcher. HR-only endpoints are role-gated so
// SUPERVISOR (sees /approvals but not HR routes) doesn't trigger 403s.
export type MenuBadgeKey = 'approvals' | 'emergency' | 'urgentLeaves' | 'pendingChanges' | 'tickets' | 'approvalsTotal' | 'escalations';

export function useMenuBadgeCounts(role: string): Record<MenuBadgeKey, number> {
  const isAdmin = role === 'OWNER' || role === 'HR' || role === 'MANAGER';
  const isSupervisorPlus = isAdmin || role === 'SUPERVISOR';

  const approvals = useQuery({
    queryKey: ['approvals', 'pending'],
    // backend ตอบ { leaves, ots, shift_changes } (ไม่ใช่ array) — queryKey เดียวกับหน้า /approvals
    queryFn: () => apiFetch<{ leaves?: unknown[]; ots?: unknown[]; shift_changes?: unknown[]; shift_swaps?: unknown[]; shift_times?: unknown[] }>('/approvals/pending'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isSupervisorPlus,
  });
  const urgent = useEmergencyApprovals(isAdmin);
  const emergency = useQuery({
    queryKey: ['emergency-pending'],
    queryFn: () => apiFetch<unknown[]>('/leave/emergency/pending'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isAdmin,
  });
  const pendingChanges = useQuery({
    queryKey: ['pending-changes'],
    queryFn: () => apiFetch<Array<{ status: string }>>('/hr/pending-changes'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isAdmin,
  });
  const tickets = useQuery({
    queryKey: ['tickets', 'all', 'all'],
    queryFn: () => apiFetch<Array<{ status: string }>>('/tickets'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isAdmin,
  });
  // กิจกรรมผิดปกติค้าง — ใช้ queryKey เดียวกับหน้า /hr/escalations ให้แชร์ cache กัน
  // (endpoint นับทุกแถวแบบ paginate จึง refetch ห่างกว่า badge ตัวอื่น)
  const escPeriod = currentPeriod();
  const escalations = useQuery({
    // queryKey ต้องมี period ให้ตรงกับ useEscalationSummary(period) จะได้แชร์ cache
    // กับหน้า /hr/escalations ที่ default เป็นเดือนนี้เหมือนกัน
    queryKey: ['escalations-summary', escPeriod],
    queryFn: () => apiFetch<EscalationSummary>(`/escalations/summary?period=${encodeURIComponent(escPeriod)}`),
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: isAdmin,
  });

  // คำขอรับรองเวลาเข้างาน — OWNER/HR เห็นทุกขั้น · หัวหน้าเห็นขั้น 1 ของลูกทีม (fb#429) · queryKey เดียวกับแท็บ "รับรองเวลา"
  // (fb#79: เดิมไม่นับเข้า badge เลย HR ไม่รู้ว่ามีคำขอค้าง)
  const uid = useAuthStore((s) => s.profile?.user_id ?? '');
  const corrections = useQuery({
    queryKey: ['hr', 'attendance-correction', 'pending', uid],
    queryFn: () => apiFetch<unknown[]>('/hr/attendance-corrections'),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: isSupervisorPlus,
  });

  const correctionsCount = isSupervisorPlus && Array.isArray(corrections.data) ? corrections.data.length : 0;
  // fb#79 (tester): เดิมเช็ค Array.isArray กับ object → ใบลา/OT/ย้ายวันหยุดไม่เคยถูกนับเข้า badge
  const ap = approvals.data;
  const leaveOtCount = ap ? (ap.leaves?.length ?? 0) + (ap.ots?.length ?? 0) + (ap.shift_changes?.length ?? 0) + (ap.shift_swaps?.length ?? 0) + (ap.shift_times?.length ?? 0) : 0;
  const approvalsCount = leaveOtCount + correctionsCount;
  const emergencyCount = Array.isArray(emergency.data) ? emergency.data.length : 0;
  // fb#484: ใบลาติดป้ายลาฉุกเฉิน (นับอยู่ใน approvals แล้ว — ใช้แค่เลขบนแท็บลาฉุกเฉิน ห้ามบวกซ้ำใน approvalsTotal)
  const urgentLeavesCount = urgent.data?.leaves?.length ?? 0;
  const pendingChangesCount = Array.isArray(pendingChanges.data)
    ? pendingChanges.data.filter((c) => c.status === 'pending').length
    : 0;
  const ticketsCount = Array.isArray(tickets.data)
    ? tickets.data.filter((t) => t.status === 'open' || t.status === 'under_review' || t.status === 'escalated').length
    : 0;
  return {
    approvals: approvalsCount,
    emergency: emergencyCount,
    urgentLeaves: urgentLeavesCount,
    pendingChanges: pendingChangesCount,
    tickets: ticketsCount,
    // Sum used by the consolidated "อนุมัติคำขอ" menu entry (single button
    // replaces 4 separate buttons; SUPERVISOR only contributes approvals).
    approvalsTotal: approvalsCount + emergencyCount + pendingChangesCount + ticketsCount,
    escalations: escalations.data?.total ?? 0,
  };
}

// === Reset tenant data (OWNER only) ===
export interface ResetPreview {
  work: Array<{ label: string; count: number }>;
  setup: Array<{ label: string; count: number }>;
}
export function useResetData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { mode: 'work' | 'all'; confirm: string }) =>
      apiFetch<{ ok: boolean; result: unknown }>('/owner/reset-data', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

// === Workflow config (OWNER/HR) — ผังขั้นตอนสะท้อน config จริง ===
export interface ApproverLevel { label: string; roleKind: 'supervisor' | 'admin' }
export interface WorkflowConfig {
  approvalLevels: number;
  approverLevels: ApproverLevel[];
  usesShifts: boolean;
  usesCommission: boolean;
  usesLeavePackages: boolean;
}
export function useWorkflowConfig() {
  return useQuery({
    queryKey: ['workflow-config'],
    queryFn: () => apiFetch<WorkflowConfig>('/owner/workflow-config'),
    staleTime: 5 * 60_000,
  });
}

// === Payroll Blueprint (OWNER/HR) — config snapshot ทั้งบริษัทในที่เดียว ===
export interface SlipLineItem { label: string; amount: number }
export interface Blueprint {
  generated_at: string;
  company: { name: string; tax_id: string | null; address: string | null; phone: string | null; signer_name: string | null; signer_title: string | null; has_logo: boolean };
  pay_cycle: { cutoff_day: number | null; payday_day: number | null; backdated_mode: string | null; timezone: string };
  work_rules: { workdays_per_week: number; weekend_days: number[]; hours_per_workday: number; late_grace_minutes: number; ot_weekly_cap_hours: number; solo_mode: boolean };
  statutory: {
    year: number;
    sso: { rate_percent: number | null; ceiling: number | null; monthly_cap: number | null };
    personal_allowance: number;
    expense_allowance_note: string;
    min_wage: number | null;
    ot_multipliers: { ot1: number; holiday_work_monthly: number; holiday_work_daily: number; holiday_ot: number };
    tax_brackets_count: number;
  };
  workforce: { total_active: number; monthly: number; daily: number; salary_covered: number; salary_missing: Array<{ emp_code: string; name: string }> };
  allowances: Array<{ name: string; preset: string; taxable: boolean; ssoable: boolean; pfable: boolean; frequency: string; default_amount: number | null; default_rate: number | null; eligible_count: number }>;
  deductions: { statutory: Array<{ name: string; kind: string; default_amount: number | null; eligible_count: number }>; fixed: Array<{ name: string; kind: string; default_amount: number | null; eligible_count: number }>; section76_note: string };
  shifts: Array<{ name: string; start: string; end: string; crosses_midnight: boolean; night_premium_type: string; night_premium_value: number | null }>;
  leave: { packages: Array<{ name: string; sick: number | null; personal: number | null; vacation: number | null; prorate_on_join: boolean; carry_over_max: number | null }>; assigned_count: number };
  approval: { levels: number; by_level: Array<{ level: number; approvers: Array<{ name: string; role: string }> }>; rules: Array<{ kind: string; leave_type: string | null; ot_type: string | null; required_levels: number }>; notify: { employees_with_approver: number; employees_using_fallback: number; fallback_role: 'hr' | 'owner' | 'none'; fallback_recipients: string[] } };
  holidays: { year: number; count: number; items: Array<{ date: string; name: string; type: string }> };
  commission: { uses: boolean; packages: Array<{ name: string; basis: string; tiers: number }> };
  sample_slip: { period: string; line_items: { earnings?: SlipLineItem[]; deductions?: SlipLineItem[]; earnings_total?: number; deductions_total?: number } } | null;
  readiness: { pct: number; complete: boolean; items: Array<{ key: string; label: string; done: boolean; route: string; optional?: boolean }>; next_step: { key: string; label: string; route: string } | null };
}
export function useBlueprint() {
  return useQuery({
    queryKey: ['blueprint'],
    queryFn: () => apiFetch<Blueprint>('/owner/blueprint'),
    staleTime: 2 * 60_000,
  });
}

// ─── Compensation (โครงสร้างค่าตอบแทน) — PayPilot = SoT (ADR 0003) — /hr/comp/* ───
// Ported from eval-pilot; PayPilot now owns all compensation tables.

export type CompStrategy = 'aggressive' | 'fair' | 'lean' | 'custom';
export interface CompBand {
  id: string;
  position_id: string;
  min_salary: number | null;
  mid_salary: number | null;
  max_salary: number | null;
  currency: string;
  strategy: CompStrategy | null;
  effective_date?: string;
  is_archived?: boolean;
  /** เรื่อง 3b — true = ช่วงเงินเดือนถูกมาส์กสำหรับ role นี้ (min/mid/max = null) */
  pay_masked?: boolean;
}
export function useCompBands() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-bands'], queryFn: () => apiFetch<CompBand[]>('/hr/comp/bands') });
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? apiFetch<CompBand>(`/hr/comp/bands/${id}`, { method: 'PATCH', body })
        : apiFetch<CompBand>('/hr/comp/bands', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-bands'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ archived: boolean }>(`/hr/comp/bands/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-bands'] }),
  });
  return { ...list, save, del };
}

// salary_structure cell: min/q1/q2(mid)/q3/max per (department × level).
export interface CompStructureCell {
  id: string;
  department_id: string;
  level: number;
  min_salary: number | null;
  q1_salary: number | null;
  q2_salary: number | null;
  q3_salary: number | null;
  max_salary: number | null;
}
export function useCompStructure() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-structure'], queryFn: () => apiFetch<CompStructureCell[]>('/hr/comp/structure') });
  const upsert = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<CompStructureCell>('/hr/comp/structure', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-structure'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/comp/structure/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-structure'] }),
  });
  // AI-fill writes the grid directly server-side and returns counts.
  const aiFill = useMutation({
    mutationFn: (body: { strategy: string }) =>
      apiFetch<{ upserted: number; errors: unknown[]; total: number }>('/hr/comp/structure/ai-fill', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-structure'] }),
  });
  return { ...list, upsert, del, aiFill };
}

export interface BenefitLevers {
  raise_pct?: number;
  bonus_months?: number;
  reward_bonus?: number;          // เงินรางวัล/โบนัสพิเศษ (บาท, จ่ายครั้งเดียว/ปี)
  health_insurance?: number;      // ประกันสุขภาพ (บาท/ปี)
  life_insurance?: number;        // ประกันชีวิต/อุบัติเหตุ (บาท/ปี)
  provident_fund_pct?: number;
  allowance?: number;             // เบี้ยเลี้ยง (บาท/เดือน)
  travel_allowance?: number;      // ค่าเดินทาง (บาท/เดือน)
  phone_allowance?: number;       // ค่าโทรศัพท์ (บาท/เดือน)
  position_allowance?: number;    // ค่าตำแหน่ง (บาท/เดือน)
  meal_allowance?: number;        // ค่าอาหาร (บาท/เดือน)
  housing_allowance?: number;     // ค่าที่พัก (บาท/เดือน)
  uniform_allowance?: number;     // ค่าชุด/ยูนิฟอร์ม (บาท/ปี)
}
export interface BenefitPackage {
  id: string;
  name: string;
  description: string | null;
  levers: BenefitLevers;
  bonus_basis: string | null;
  status: string;
  assignment_count?: number;
}
export interface BenefitAssignment {
  id: string;
  employee_id: string;
  employee_name?: string;
  computed_cost: number;
  status: string;
}
export function useCompBenefits() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-benefits'], queryFn: () => apiFetch<BenefitPackage[]>('/hr/comp/benefits') });
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? apiFetch<BenefitPackage>(`/hr/comp/benefits/${id}`, { method: 'PATCH', body })
        : apiFetch<BenefitPackage>('/hr/comp/benefits', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-benefits'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ archived: boolean }>(`/hr/comp/benefits/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-benefits'] }),
  });
  const assign = useMutation({
    mutationFn: ({ id, employee_ids }: { id: string; employee_ids: string[] }) =>
      apiFetch<{ assigned: number }>(`/hr/comp/benefits/${id}/assign`, { method: 'POST', body: { employee_ids } }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['comp-benefits'] });
      qc.invalidateQueries({ queryKey: ['comp-benefit', v.id] });
    },
  });
  const unassign = useMutation({
    mutationFn: (aid: string) => apiFetch<{ removed: boolean }>(`/hr/comp/benefits/assignments/${aid}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comp-benefits'] });
      qc.invalidateQueries({ queryKey: ['comp-benefit'] });
    },
  });
  return { ...list, save, del, assign, unassign };
}
export function useBenefitDetail(id: string | null) {
  return useQuery({
    queryKey: ['comp-benefit', id],
    queryFn: () => apiFetch<BenefitPackage & { assignments: BenefitAssignment[] }>(`/hr/comp/benefits/${id}`),
    enabled: !!id,
  });
}

export interface CompComponent {
  type?: string;
  amount?: number;
  frequency?: string;
}
export interface CompPackage {
  id: string;
  position_id: string;
  base_salary: number;
  components: CompComponent[];
  variable_total: number;
  benefit_total: number;
  ctc_total: number;
  notes: string | null;
}
export function useCompPackages() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-packages'], queryFn: () => apiFetch<CompPackage[]>('/hr/comp/packages') });
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? apiFetch<CompPackage>(`/hr/comp/packages/${id}`, { method: 'PATCH', body })
        : apiFetch<CompPackage>('/hr/comp/packages', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-packages'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/comp/packages/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-packages'] }),
  });
  return { ...list, save, del };
}

export interface VariablePay {
  id: string;
  position_id: string | null;
  level: number | null;
  name: string;
  type: string;
  formula: Record<string, unknown>;
  frequency: string | null;
  notes: string | null;
}
export function useCompVariablePay() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['comp-variable-pay'], queryFn: () => apiFetch<VariablePay[]>('/hr/comp/variable-pay') });
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? apiFetch<VariablePay>(`/hr/comp/variable-pay/${id}`, { method: 'PATCH', body })
        : apiFetch<VariablePay>('/hr/comp/variable-pay', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-variable-pay'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ deleted: boolean }>(`/hr/comp/variable-pay/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-variable-pay'] }),
  });
  return { ...list, save, del };
}

// === HR: Leave Usage Audit (read-only) ===
export interface LeaveUsageEmployee {
  emp_code: string;
  name: string;
  nickname: string;
  department: string;
  position: string;
  hours_per_day: number;
  by_type: Record<string, number>;
  total_days: number;
  /** สิทธิ์ที่ตั้งไว้ปีนั้น — null = ยังไม่ได้ตั้งสิทธิ์ให้คนนี้ */
  quota: { sick: number; personal: number; vacation: number } | null;
  /** คงเหลือ = สิทธิ์ − ใช้ไป (null เมื่อยังไม่ได้ตั้งสิทธิ์) */
  remaining: { sick: number; personal: number; vacation: number } | null;
}
export function useLeaveUsage(year: number) {
  return useQuery({
    queryKey: ['hr', 'leave-usage', year],
    queryFn: () => apiFetch<{ year: number; employees: LeaveUsageEmployee[] }>(`/hr/leave-usage?year=${year}`),
  });
}
export interface LeaveUsageRecord {
  leave_id: string;
  date: string;
  leave_type: string;
  period: string;
  start_time: string | null;
  end_time: string | null;
  status: string;
  days: number;
}
export function useLeaveUsageRecords(empCode: string | null, year: number) {
  return useQuery({
    queryKey: ['hr', 'leave-usage', 'records', empCode, year],
    enabled: !!empCode,
    queryFn: () =>
      apiFetch<{ emp_code: string; year: number; hours_per_day: number; records: LeaveUsageRecord[] }>(
        `/hr/leave-usage/records?emp_code=${encodeURIComponent(empCode ?? '')}&year=${year}`,
      ),
  });
}

// === HR: ครบทดลองงาน (probation review) ===
export type ProbationBucket = 'later' | 'upcoming' | 'due' | 'overdue';

export interface ProbationQueueRow {
  emp_code: string;
  emp_name: string;
  position: string | null;
  department: string | null;
  start_date: string;
  probation_end_date: string;
  probation_extend_count: number;
  days_left: number;
  bucket: ProbationBucket;
}

export interface ProbationListResponse {
  settings: { probation_months: number; probation_notice_days: number };
  today: string;
  rows: ProbationQueueRow[];
  /** อยู่ในทดลองงานแต่ไม่มีวันเริ่มงาน → คำนวณวันครบไม่ได้ ต้องให้ HR เติมก่อน */
  unknown_start: Array<{ emp_code: string; emp_name: string; department: string | null }>;
  counts: { unknown_start: number; overdue: number; due: number; upcoming: number; later: number; total_pending: number };
}

export interface ProbationHistoryRow {
  emp_code: string;
  emp_name: string;
  action: 'pass' | 'extend' | 'fail' | 'waive';
  old_end_date: string | null;
  new_end_date: string | null;
  note: string | null;
  decided_by_name: string | null;
  created_at: string;
}

export function useProbationList(scope: 'queue' | 'all' = 'queue') {
  return useQuery({
    queryKey: ['hr', 'probation', scope],
    queryFn: () => apiFetch<ProbationListResponse>(`/hr/probation/list?scope=${scope}`),
    refetchInterval: 60_000,
  });
}

export function useProbationHistory(enabled = true) {
  return useQuery({
    queryKey: ['hr', 'probation', 'decided'],
    queryFn: () =>
      apiFetch<{ settings: ProbationListResponse['settings']; today: string; history: ProbationHistoryRow[] }>(
        '/hr/probation/list?scope=decided',
      ),
    enabled,
  });
}

export function useDecideProbation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      emp_code: string;
      action: 'pass' | 'extend' | 'fail' | 'waive';
      new_end_date?: string;
      note?: string;
    }) =>
      apiFetch<{ emp_code: string; emp_name: string; action: string; probation_status: string }>(
        `/hr/probation/${encodeURIComponent(args.emp_code)}/decide`,
        { method: 'POST', body: { action: args.action, new_end_date: args.new_end_date, note: args.note } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'probation'] });
      qc.invalidateQueries({ queryKey: ['hr', 'employees'] });
    },
  });
}

// === สัญญาจ้าง / สัญญาทดลองงาน (FM-HR-001) ===

export type ContractStatus = 'draft' | 'sent' | 'viewed' | 'signed' | 'cancelled';
export type ContractKind = 'permanent' | 'fixed_term' | 'probation';

export interface ContractRow {
  id: string;
  emp_code: string;
  name: string;
  doc_no: string;
  contract_kind: ContractKind;
  status: ContractStatus;
  start_date: string | null;
  end_date: string | null;
  probation_end_date: string | null;
  sent_at: string | null;
  signed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  /** ช่องสำคัญที่ว่างในสัญญาฉบับนั้น (เช่น เลขบัตรประชาชน) */
  missing_fields?: string[];
}

export function useContracts(filter?: { emp?: string; status?: ContractStatus }) {
  const qs = new URLSearchParams();
  if (filter?.emp) qs.set('emp', filter.emp);
  if (filter?.status) qs.set('status', filter.status);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['hr', 'contracts', filter?.emp ?? '', filter?.status ?? ''],
    queryFn: () =>
      apiFetch<{ contracts: ContractRow[]; unavailable?: string }>(`/hr/contracts${suffix}`),
    staleTime: 30_000,
  });
}

export interface CreateContractBody {
  emp_code: string;
  contract_kind: ContractKind;
  start_date: string;
  end_date?: string;
  probation_days?: number;
  salary_amount?: number;
  fields?: {
    workplace?: string;
    report_to?: string;
    renewal_term?: string;
    payday_text?: string;
    extra_compensation?: string;
    duties?: string[];
  };
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContractBody) =>
      apiFetch<{ contract: { id: string; doc_no: string }; missing_fields?: string[] }>(
        '/hr/contracts', { method: 'POST', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'contracts'] }),
  });
}

export function useSendContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; line_pushed: boolean; message: string }>(
        `/hr/contracts/${encodeURIComponent(id)}/send`, { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'contracts'] }),
  });
}

export function useCancelContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      apiFetch<{ ok: boolean; message: string }>(
        `/hr/contracts/${encodeURIComponent(args.id)}/cancel`,
        { method: 'POST', body: { reason: args.reason } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'contracts'] }),
  });
}

// --- ฝั่งพนักงาน ---

export interface MyContractRow {
  id: string;
  doc_no: string;
  contract_kind: ContractKind;
  status: ContractStatus;
  start_date: string | null;
  end_date: string | null;
  sent_at: string | null;
  signed_at: string | null;
}

export function useMyContracts() {
  return useQuery({
    queryKey: ['me', 'contracts'],
    queryFn: () => apiFetch<{ contracts: MyContractRow[] }>('/me/contracts'),
    staleTime: 30_000,
  });
}

export interface ContractClauseView { no: number; title: string; paragraphs: string[] }
export interface ContractSectionView { heading: string; clauses: ContractClauseView[] }

export interface MyContractDetail {
  contract: {
    id: string;
    doc_no: string;
    status: ContractStatus;
    issued_at: string;
    fields: Record<string, unknown>;
    sections: ContractSectionView[];
    content_hash: string | null;
  };
  company: {
    name: string; address: string; taxId: string; phone: string;
    signerName: string; signerTitle: string;
  };
  signatures: { role: string; name: string; signed_at?: string }[];
}

export function useMyContract(id: string | undefined) {
  return useQuery({
    queryKey: ['me', 'contract', id ?? ''],
    queryFn: () => apiFetch<MyContractDetail>(`/me/contracts/${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}

export function useRequestContractOtp() {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; expires_in_seconds: number }>(
        `/me/contracts/${encodeURIComponent(id)}/otp`, { method: 'POST', body: {} },
      ),
  });
}

export function useSignContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; otp: string; signature_data_url: string; acknowledged: true }) =>
      apiFetch<{ ok: boolean; signed_at: string; content_hash: string; resumed?: boolean }>(
        `/me/contracts/${encodeURIComponent(args.id)}/sign`,
        {
          method: 'POST',
          body: {
            otp: args.otp,
            signature_data_url: args.signature_data_url,
            // เจตนายอมรับต้องเดินทางไปถึงเซิร์ฟเวอร์และถูกบันทึก ไม่ใช่อยู่แค่ในจอ
            acknowledged: args.acknowledged,
          },
        },
      ),
    onSuccess: (_d, args) => {
      qc.invalidateQueries({ queryKey: ['me', 'contracts'] });
      qc.invalidateQueries({ queryKey: ['me', 'contract', args.id] });
    },
  });
}

// === ข้อบังคับ / ระเบียบ / ประกาศ ===

export type PolicyKind = 'work_rule' | 'regulation' | 'announcement';
export type PolicyStatus = 'draft' | 'published' | 'archived';
export type PolicyAckMode = 'button' | 'signature';
export type PolicyAudience =
  | { scope: 'all' }
  | { scope: 'dept'; dept_ids: string[] }
  | { scope: 'branch'; branch_ids: string[] }
  | { scope: 'emp'; emp_codes: string[] };

export interface PolicyRow {
  id: string;
  kind: PolicyKind;
  doc_no: string;
  title: string;
  summary: string | null;
  version: number;
  supersedes_id: string | null;
  file_name: string | null;
  file_size: number | null;
  effective_from: string;
  effective_to: string | null;
  audience: PolicyAudience;
  ack_required: boolean;
  ack_mode: PolicyAckMode;
  status: PolicyStatus;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  acked_count: number;
  total_count: number;
}

export interface PolicyFileInput {
  file_name: string;
  file_size: number;
  content_type: 'application/pdf';
  file_sha256: string;
}

export interface CreatePolicyInput extends PolicyFileInput {
  kind: PolicyKind;
  title: string;
  summary?: string | null;
  effective_from: string;
  effective_to?: string | null;
  audience: PolicyAudience;
  ack_required: boolean;
  ack_mode: PolicyAckMode;
}

export interface PolicyDraftResponse {
  policy: { id: string; doc_no: string; version: number; status: PolicyStatus };
  upload: { url: string; path: string; content_type: string; expires_in_seconds: number };
}

export function usePolicies(filter?: { kind?: PolicyKind; status?: PolicyStatus }) {
  const qs = new URLSearchParams();
  if (filter?.kind) qs.set('kind', filter.kind);
  if (filter?.status) qs.set('status', filter.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery({
    queryKey: ['hr', 'policies', filter?.kind ?? '', filter?.status ?? ''],
    queryFn: () => apiFetch<{ policies: PolicyRow[]; unavailable?: string }>(`/hr/policies${suffix}`),
    staleTime: 30_000,
  });
}

export function useCreatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePolicyInput) => apiFetch<PolicyDraftResponse>('/hr/policies', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'policies'] }),
  });
}

export function useCreatePolicyVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: Omit<CreatePolicyInput, 'kind'> }) =>
      apiFetch<PolicyDraftResponse>(`/hr/policies/${encodeURIComponent(args.id)}/new-version`, {
        method: 'POST', body: args.body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'policies'] }),
  });
}

export function usePublishPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean; published_at: string }>(
      `/hr/policies/${encodeURIComponent(id)}/publish`, { method: 'POST', body: {} },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'policies'] });
      qc.invalidateQueries({ queryKey: ['me', 'policies'] });
    },
  });
}

export function useArchivePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(
      `/hr/policies/${encodeURIComponent(id)}/archive`, { method: 'POST', body: {} },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'policies'] });
      qc.invalidateQueries({ queryKey: ['me', 'policies'] });
    },
  });
}

export interface PolicyAckPerson {
  emp_code: string;
  name: string;
  department: string | null;
  position?: string | null;
  acked_at?: string;
  read_seconds?: number | null;
  signed?: boolean;
}

export interface PolicyAcksResponse {
  policy: Pick<PolicyRow, 'id' | 'doc_no' | 'title' | 'kind' | 'version' | 'status' | 'effective_from' | 'effective_to' | 'ack_required' | 'ack_mode'>;
  summary: { acknowledged: number; pending: number; total: number };
  acknowledged: PolicyAckPerson[];
  pending: PolicyAckPerson[];
}

export function usePolicyAcks(id: string | undefined) {
  return useQuery({
    queryKey: ['hr', 'policy', id ?? '', 'acks'],
    queryFn: () => apiFetch<PolicyAcksResponse>(`/hr/policies/${encodeURIComponent(id!)}/acks`),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useRemindPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean; pending: number; sent: number; unavailable: number; failed: number }>(
      `/hr/policies/${encodeURIComponent(id)}/remind`, { method: 'POST', body: {} },
    ),
    onSuccess: (_data, id) => qc.invalidateQueries({ queryKey: ['hr', 'policy', id, 'acks'] }),
  });
}

export interface MyPolicyRow {
  id: string;
  kind: PolicyKind;
  doc_no: string;
  title: string;
  summary: string | null;
  version: number;
  effective_from: string;
  effective_to: string | null;
  ack_required: boolean;
  ack_mode: PolicyAckMode;
  published_at: string | null;
  acked: boolean;
  acked_at: string | null;
}

export function useMyPolicies() {
  return useQuery({
    queryKey: ['me', 'policies'],
    queryFn: () => apiFetch<{ policies: MyPolicyRow[]; pending_count: number }>('/me/policies'),
    staleTime: 30_000,
  });
}

export interface MyPolicyDetail {
  policy: MyPolicyRow & { file_name: string | null; file_url: string };
}

export function useMyPolicy(id: string | undefined) {
  return useQuery({
    queryKey: ['me', 'policy', id ?? ''],
    queryFn: () => apiFetch<MyPolicyDetail>(`/me/policies/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
}

export function useAcknowledgePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; read_seconds: number; signature_svg?: string }) =>
      apiFetch<{ ok: boolean; already_acked: boolean; ack: { id: string; acked_at: string } }>(
        `/me/policies/${encodeURIComponent(args.id)}/ack`,
        { method: 'POST', body: { read_seconds: args.read_seconds, signature_svg: args.signature_svg } },
      ),
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['me', 'policies'] });
      qc.invalidateQueries({ queryKey: ['me', 'policy', args.id] });
      qc.invalidateQueries({ queryKey: ['hr', 'policy', args.id, 'acks'] });
    },
  });
}

// ════════════════════════════════════════════════════════════
// หนังสือ HR ที่ระบบออกให้ — รับรองการทำงาน · รับรองเงินเดือน · หนังสือเตือน
//
// ⭐ API แยกเจ้าของข้อมูลเป็น 3 ก้อน: registry (ทะเบียนเป็นเจ้าของ) · input (HR พิมพ์เอง)
//    · computed (ระบบคิด) — จอห้าม merge เอง ให้ใช้ค่าที่ server ส่งมาตรง ๆ
// ⭐ ทุก action ที่แก้ร่างต้องส่ง expected_revision · ตอนออกจริงต้องส่ง expected_hash
//    เพื่อยืนยันว่ากำลังออกฉบับเดียวกับที่ตาเห็น
// ════════════════════════════════════════════════════════════
export type LetterType = 'employment_cert' | 'salary_cert' | 'warning';
export type LetterStatus = 'draft' | 'issued' | 'cancelled';
export type WageUnit = 'month' | 'day' | 'hour';

export interface WarningInput {
  level?: 'first' | 'second' | 'final';
  category?: 'late' | 'absent' | 'leave_post' | 'rule_violation' | 'misconduct' | 'performance' | 'other';
  categoryOther?: string;
  incidentDate?: string;
  incidentDetail?: string;
  ruleRef?: string;
  priorActions?: string;
  requiredAction?: string;
  witnessName?: string;
}

export const WARNING_CATEGORY_LABEL: Record<NonNullable<WarningInput['category']>, string> = {
  late: 'มาสาย / กลับก่อนเวลา',
  absent: 'ขาดงานโดยไม่แจ้ง',
  leave_post: 'ละทิ้งหน้าที่ระหว่างเวลางาน',
  rule_violation: 'ฝ่าฝืนข้อบังคับหรือคำสั่งโดยชอบ',
  misconduct: 'ประพฤติตนไม่เหมาะสม',
  performance: 'บกพร่องต่อหน้าที่ / ผลงานไม่ได้ตามมาตรฐาน',
  other: 'อื่น ๆ (ระบุ)',
};

export const WARNING_LEVEL_LABEL: Record<NonNullable<WarningInput['level']>, string> = {
  first: 'ครั้งที่ 1',
  second: 'ครั้งที่ 2',
  final: 'ครั้งสุดท้าย',
};

export const WAGE_UNIT_LABEL: Record<WageUnit, string> = {
  month: 'เดือน',
  day: 'วัน',
  hour: 'ชั่วโมง',
};

export interface LetterRegistry {
  asOf: string;
  empNameTh: string;
  empCode: string;
  position?: string;
  department?: string;
  startDate?: string;
  employmentStatus: 'active' | 'probation' | 'resigned' | 'terminated';
  endDate?: string;
  employeeType: 'monthly' | 'daily' | 'hourly';
  wage?: { amount: number; unit: WageUnit; effectiveDate?: string };
  nationalIdMasked?: string;
  company: { name: string; address: string; taxId: string; phone: string; signerName: string; signerTitle: string };
}

export interface LetterInput {
  purpose?: string;
  salaryOverride?: { amount: number; reason: string };
  allowance?: { label: string; amount: number };
  warning?: WarningInput | null;
  /** fb#645 ผู้ลงนามแทนเฉพาะฉบับ (HR/เลขานุการ) — ไม่มี = ผู้มีอำนาจจากตั้งค่าบริษัท */
  signer?: { name: string; title: string } | null;
}

export interface LetterComputed {
  employmentStatement: 'currently_employed' | 'former_employee';
  tenureText: string;
  periodText: string;
  certifiedWage?: { amount: number; unit: WageUnit; origin: 'registry' | 'hr_override' };
  warningValidUntil?: string;
}

/** ช่องที่ยังขาด พร้อมบอกว่าไปแก้ที่ไหน — จอต้องพาไปหน้าที่ถูก ไม่ใช่ให้เดา */
export interface LetterMissing {
  label: string;
  /** ต้องตรงกับฝั่ง worker (letter-fields.ts) — 'company' = หน้าตั้งค่าบริษัท ไม่ใช่ทะเบียนพนักงาน */
  fixWhere: 'registry' | 'company' | 'form';
}

export interface LetterRow {
  id: string;
  emp_code: string;
  letter_type: LetterType;
  title: string;
  status: LetterStatus;
  doc_no: string | null;
  as_of_date: string | null;
  revision: number;
  view_hash: string | null;
  issued_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  registry: LetterRegistry;
  input: LetterInput;
  computed: LetterComputed;
  missing: LetterMissing[];
  /** มีเฉพาะผลลัพธ์ของ refresh — บอกว่าทะเบียนเปลี่ยนอะไรไปบ้าง */
  changes?: { label: string; before: string; after: string }[];
  /** หลักฐานรับทราบ (หนังสือเตือน) — null = ยังไม่มี · มีเฉพาะในรายการ (fb#176) */
  ack?: LetterAck | null;
  /** หนังสือเตือนที่ออกจริง ยังไม่ยกเลิก และยังไม่มีหลักฐานรับทราบ */
  needs_ack?: boolean;
}

/** หลักฐานรับทราบหนังสือ — screen = พนักงานเซ็นบนจอ · paper = HR บันทึกว่าลงนามกระดาษ */
export interface LetterAck {
  method: 'screen' | 'paper';
  acked_at: string;
  signed_on: string | null;
  has_file: boolean;
}

/** HR บันทึกว่าพนักงานลงนามบนกระดาษแล้ว (ทางสำรองเมื่อไม่เซ็นบนจอ) */
/** ลิงก์ชั่วคราวเปิดสแกนฉบับลงนามกระดาษ */
export async function fetchAckFileUrl(id: string): Promise<string> {
  return (await apiFetch<{ url: string }>(`/hr/letters/${encodeURIComponent(id)}/ack-file`)).url;
}

export function useRecordPaperAck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; signed_on: string; file_path?: string }) =>
      apiFetch<{ ok: boolean; ack: LetterAck }>(`/hr/letters/${encodeURIComponent(args.id)}/ack-paper`, {
        method: 'POST',
        body: { signed_on: args.signed_on, file_path: args.file_path },
      }),
    onSettled: () => invalidateLetters(qc),
  });
}

export interface LetterPreview {
  letter_type: LetterType;
  title: string;
  registry: LetterRegistry;
  computed: LetterComputed;
  missing: LetterMissing[];
  view_hash: string;
}

/** ทุกรายการที่ยาวได้ตอบกลับเป็นก้อนนี้ — `has_more` คือของที่ถูกตัดออกจากจอ */
export type LetterPage<T> = { items: T[]; has_more: boolean; limit: number };

export function useLetters(filter?: { emp?: string; type?: LetterType; status?: LetterStatus }) {
  const qs = new URLSearchParams();
  if (filter?.emp) qs.set('emp', filter.emp);
  if (filter?.type) qs.set('type', filter.type);
  if (filter?.status) qs.set('status', filter.status);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['hr', 'letters', filter?.emp ?? '', filter?.type ?? '', filter?.status ?? ''],
    queryFn: () => apiFetch<LetterPage<LetterRow>>(`/hr/letters${suffix}`),
    staleTime: 30_000,
  });
}

export function useLetter(id: string | undefined) {
  return useQuery({
    queryKey: ['hr', 'letter', id ?? ''],
    queryFn: () => apiFetch<LetterRow>(`/hr/letters/${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}

/**
 * ดูข้อมูลที่จะขึ้นบนกระดาษ **ก่อน** สร้างร่าง — จอใช้ค่านี้ตั้งป้ายหน่วยเงินและ
 * ตัดสินว่าจะโชว์ช่องไหน (บั๊กเดิม: จอเดาเองว่า "บาท/เดือน" เสมอ)
 */
export function useLetterPreview(args: {
  emp?: string;
  type?: LetterType;
  /** โหมดแก้ร่าง — คิดบน snapshot ที่บันทึกไว้ ไม่จับทะเบียนสดมาใหม่ */
  draftId?: string;
  input?: Record<string, unknown>;
}) {
  const enabled = !!args.draftId || (!!args.emp && !!args.type);
  // ส่งสิ่งที่กำลังกรอกไปด้วย เพื่อให้ "ยังกรอกไม่ครบ" ที่จอโชว์ ตรงกับด่านฝั่งเซิร์ฟเวอร์จริง
  // (เดิมพรีวิวคำนวณด้วยค่าว่างเสมอ จอจึงบอกว่าออกไม่ได้ทั้งที่ HR ระบุยอดเองไว้แล้ว)
  const inputKey = args.input && Object.keys(args.input).length ? JSON.stringify(args.input) : '';
  const base = args.draftId
    ? `/hr/letters/preview?draft=${encodeURIComponent(args.draftId)}`
    : `/hr/letters/preview?emp=${encodeURIComponent(args.emp ?? '')}&type=${encodeURIComponent(args.type ?? '')}`;
  return useQuery({
    queryKey: ['hr', 'letter-preview', args.draftId ?? '', args.emp ?? '', args.type ?? '', inputKey],
    queryFn: () =>
      apiFetch<LetterPreview>(base + (inputKey ? `&input=${encodeURIComponent(inputKey)}` : '')),
    enabled,
  });
}

function invalidateLetters(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['hr', 'letters'] });
  qc.invalidateQueries({ queryKey: ['hr', 'letter'] });
}

export function useCreateLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { emp_code: string; letter_type: LetterType; input?: LetterInput }) =>
      apiFetch<LetterRow>('/hr/letters', { method: 'POST', body }),
    onSuccess: () => invalidateLetters(qc),
  });
}

/** แก้เฉพาะค่าที่ HR เป็นเจ้าของ — ส่งช่องไหนแก้ช่องนั้น ไม่ส่ง = คงเดิม */
export function usePatchLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; expected_revision: number; input: Partial<LetterInput> }) =>
      apiFetch<LetterRow>(`/hr/letters/${encodeURIComponent(args.id)}/patch`, {
        method: 'POST',
        body: { expected_revision: args.expected_revision, input: args.input },
      }),
    onSuccess: () => invalidateLetters(qc),
  });
}

/** ดึงทะเบียนใหม่ — ไม่แตะค่าที่ HR พิมพ์ และคืนรายการที่เปลี่ยนกลับมาให้ดู */
export function useRefreshLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; expected_revision: number }) =>
      apiFetch<LetterRow>(`/hr/letters/${encodeURIComponent(args.id)}/refresh`, {
        method: 'POST',
        body: { expected_revision: args.expected_revision },
      }),
    onSuccess: () => invalidateLetters(qc),
  });
}

export function useIssueLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; expected_hash: string }) =>
      apiFetch<LetterRow>(`/hr/letters/${encodeURIComponent(args.id)}/issue`, {
        method: 'POST',
        body: { expected_hash: args.expected_hash },
      }),
    // 409 = ข้อมูลไม่สด/ถูกแก้ — ต้องโหลดใหม่ให้จอตรงกับของจริงเสมอ ไม่ใช่เฉพาะตอนสำเร็จ
    onSettled: () => invalidateLetters(qc),
  });
}

export function useCancelLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      apiFetch<{ id: string; status: string }>(`/hr/letters/${encodeURIComponent(args.id)}/cancel`, {
        method: 'POST',
        body: { reason: args.reason },
      }),
    onSettled: () => invalidateLetters(qc),
  });
}

/** ฝั่งพนักงาน — เห็นเฉพาะของตัวเอง และเฉพาะฉบับที่เคยออกจริง */
export interface MyLetterRow {
  id: string;
  letter_type: LetterType;
  title: string;
  doc_no: string | null;
  status: LetterStatus;
  issued_at: string | null;
  cancelled_at: string | null;
  as_of_date: string | null;
  ack: LetterAck | null;
  needs_ack: boolean;
}

export function useMyLetters() {
  return useQuery({
    queryKey: ['me', 'letters'],
    queryFn: () => apiFetch<LetterPage<MyLetterRow>>('/me/letters'),
    staleTime: 60_000,
  });
}

/** ฉบับเดียวของตัวเอง — หน้าลงนามเปิดจากลิงก์ไลน์ ต้องไม่พึ่งรายการที่ตัดไว้ 200 แถว */
export function useMyLetter(id: string | undefined) {
  return useQuery({
    queryKey: ['me', 'letter', id ?? ''],
    queryFn: () => apiFetch<MyLetterRow>(`/me/letters/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
}

/** พนักงานเซ็นบนจอรับทราบหนังสือเตือน (fb#176) — ตัวตนจาก JWT ห้ามส่ง emp_code */
export function useAckMyLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; signature_svg: string; read_seconds: number }) =>
      apiFetch<{ ok: boolean; already_acked: boolean; ack: LetterAck }>(
        `/me/letters/${encodeURIComponent(args.id)}/ack`,
        { method: 'POST', body: { signature_svg: args.signature_svg, read_seconds: args.read_seconds } },
      ),
    onSettled: (_d, _e, args) => {
      qc.invalidateQueries({ queryKey: ['me', 'letters'] });
      qc.invalidateQueries({ queryKey: ['me', 'letter', args.id] });
    },
  });
}
